// cherish5: 배틀 조작부 한글화 5차 적용됨
// cherish3: 배틀 한글화 3차 적용됨
/**
 * Battle panel
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import {
	PS, PSRoom, type RoomOptions, type RoomID, Config, type BattlePanelLayout,
} from "./client-main";
import { PSIcon, PSPanelWrapper, PSRoomPanel } from "./panels";
import { ChatLog, ChatRoom, ChatTextEntry, ChatUserList } from "./panel-chat";
import { FormatDropdown } from "./panel-mainmenu";
import { Battle, type Pokemon, type ServerPokemon } from "./battle";
import { BattleScene } from "./battle-animations";
import { Dex, toID, type ID } from "./battle-dex";
import {
	BattleChoiceBuilder, type BattleRequestActivePokemon, type BattleRequestSideInfo,
	type BattleRequest, type BattleMoveRequest, type BattleSwitchRequest, type BattleTeamRequest,
} from "./battle-choices";
import type { Args } from "./battle-text-parser";
import { BattleTooltips, ModifiableValue } from "./battle-tooltips";
import { Net } from "./client-connection";
import { BattleLog } from "./battle-log";
import { CherishTeamChat, cherishTeamChatReceive } from "./cherish-team-chat";
import { CherishTourStrip, cherishTourHasRoom } from "./cherish-tournament";
import { CherishWeaknessGuide } from "./cherish-weakness-guide";

type BattleDesc = {
	id: RoomID,
	minElo?: number | string,
	p1?: string,
	p2?: string,
	p3?: string,
	p4?: string,
};
export class BattlesRoom extends PSRoom {
	override readonly classType = 'battles';
	/** null means still loading */
	format = '';
	filters = '';
	battles: BattleDesc[] | null = null;
	constructor(options: RoomOptions) {
		super(options);
		this.refresh();
		// If graphics preference is set to use BW sprites
		if (PS.prefs.bwgfx) {
			Dex.loadSpriteData('bw');
		}
	}
	setFormat(format: string) {
		if (format === this.format) return this.refresh();
		this.battles = null;
		this.format = format;
		this.update(null);
		this.refresh();
	}
	refresh() {
		PS.send(`/cmd roomlist ${toID(this.format)}, ${this.filters}`);
	}
}


// cherish-world: 타입 한글 표기 (표시용 — CSS 클래스는 영문 그대로)
const CHERISH_TYPE_KO: { [k: string]: string } = {
	Normal: '노말', Fire: '불꽃', Water: '물', Grass: '풀', Electric: '전기', Ice: '얼음',
	Fighting: '격투', Poison: '독', Ground: '땅', Flying: '비행', Psychic: '에스퍼', Bug: '벌레',
	Rock: '바위', Ghost: '고스트', Dragon: '드래곤', Dark: '악', Steel: '강철', Fairy: '페어리',
	Stellar: '스텔라',
};

class BattlesPanel extends PSRoomPanel<BattlesRoom> {
	static readonly id = 'battles';
	static readonly routes = ['battles'];
	static readonly Model = BattlesRoom;
	static readonly location = 'right';
	static readonly icon = <i class="fa fa-caret-square-o-right" aria-hidden></i>;
	static readonly title = '배틀 목록';
	refresh = () => {
		this.props.room.refresh();
	};
	changeFormat = (e: Event) => {
		const value = (e.target as HTMLButtonElement).value;
		this.props.room.setFormat(value);
	};
	applyFilters = (e: Event) => {
		e.preventDefault();
		const minElo = this.base?.querySelector<HTMLInputElement>(`select[name=elofilter]`)?.value;
		const searchPrefix = this.base?.querySelector<HTMLInputElement>(`input[name=prefixsearch]`)?.value;
		this.props.room.filters = `${minElo || ''},${searchPrefix || ''}`;
		this.refresh();
	};
	renderBattleLink(battle: BattleDesc) {
		const format = battle.id.split('-')[1];
		const minEloMessage = typeof battle.minElo === 'number' ? `rated ${battle.minElo}` : battle.minElo;
		return <div key={battle.id}><a href={`/${battle.id}`} class="blocklink">
			{minEloMessage && <small style="float:right">({minEloMessage})</small>}
			<small>[{format}]</small><br />
			<em class="p1">{battle.p1}</em> <small class="vs">vs.</small> <em class="p2">{battle.p2}</em>
		</a></div>;
	}
	override render() {
		const room = this.props.room;
		return <PSPanelWrapper room={room}><div class="pad">
			<button class="button" style="float:right;font-size:10pt;margin-top:3px" name="closeRoom">
				<i class="fa fa-times" aria-hidden></i> 닫기
			</button>
			<div class="roomlist">
				<p>
					<button class="button" name="refresh" onClick={this.refresh}>
						<i class="fa fa-refresh" aria-hidden></i> 새로고침
					</button> {}
					<span
						style={Dex.getPokemonIcon('meloetta-pirouette') + ';display:inline-block;vertical-align:middle'} class="picon"
						title="Meloetta is PS's mascot! The Pirouette forme is Fighting-type, and represents our battles."
					></span>
				</p>

				<p>
					<label class="label">포맷:</label><FormatDropdown onChange={this.changeFormat} placeholder="(전체 포맷)" />
				</p>
				<label class="label">
					최소 레이팅: <select name="elofilter" class="select" onChange={this.applyFilters}>
						<option value="none">없음</option><option value="1100">1100</option><option value="1300">1300</option>
						<option value="1500">1500</option><option value="1700">1700</option><option value="1900">1900</option>
					</select>
				</label>

				<form class="search" onSubmit={this.applyFilters}>
					<p>
						<input type="text" name="prefixsearch" class="textbox" placeholder="닉네임 앞글자" autocomplete="off" />
						<button type="submit" class="button">검색</button>
					</p>
				</form>
				<div class="list">{!room.battles ? (
					<p>불러오는 중...</p>
				) : !room.battles.length ? (
					<p>진행 중인 배틀이 없어요</p>
				) : (<>
					<p>{room.battles.length === 100 ?
						`100+` : room.battles.length} {`개의 배틀`}</p>
					{room.battles.map(battle => this.renderBattleLink(battle))}
				</>
				)}</div>
			</div>
		</div></PSPanelWrapper>;
	}
}

export class BattleRoom extends ChatRoom {
	override readonly classType = 'battle';
	declare pmTarget: null;
	declare challengeMenuOpen: false;
	declare challengingFormat: null;
	declare challengedFormat: null;

	override battle: Battle = null!;
	/** null if spectator, otherwise current player's info */
	side: BattleRequestSideInfo | null = null;
	request: BattleRequest | null = null;
	choices: BattleChoiceBuilder | null = null;
	autoTimerActivated: boolean | null = null;
	requireForfeit = false;
	/** should be false if we joined right after accepting or challenging a battle,
	  * and true if we refreshed and rejoined a battle.
		* null = initializing, we don't know yet */
	rejoining: boolean | null = null;
	overlayActive: 'move' | 'switch' | null = null;

	// cherish-school-coach:begin (patch-school-coach.py)
	/**
	 * 포켓몬학교 코치 표면 — 수업 카드 · 담임 말풍선 · 성적표. 서버 배너와 담임 채팅을 재료로
	 * 필드(`scene.$battle`) 안에 그린다. 로그에는 원래대로 남는다.
	 */
	cherishSchool: { lines: string[], ended: boolean, report: string[], bubbleTimer: number | null, tries: number } | null = null;
	cherishSchoolIsCoach(name: string) {
		return !!this.cherishCoachWho(name);
	}
	/** 담임 채팅 줄인가 — 「담임 체리」/「담임 베리」 (복기 코치는 유저마다 담임이 다르다). 등급 기호 접두는 뗀다. */
	cherishCoachWho(name: string) {
		const n = name.replace(/^[^A-Za-z0-9\uAC00-\uD7A3]/, '').trim();
		if (n === '담임 베리') return 'berry';
		if (n === '담임 체리') return 'cherry';
		// 담임 랭킹봇 인사 (2026-09-14) — 선생님이 배틀 참가자로 들어와 방 채팅으로 한 줄씩 말한다 (실명 계정)
		if (n === '체리') return 'cherry';
		if (n === '베리') return 'berry';
		if (n === '리치') return 'lychee';
		if (n === '피치') return 'peach';   // 2026-09-22 하위권 담임
		return '';
	}
	/**
	 * 말풍선 얼굴·이름. 그림이 없으면 onerror 로 이모지로 떨어진다.
	 * 표시는 「체리」·「베리」로 짧게 (2026-09-09 사용자 지시 — 「담임 체리」는 길다). 채팅 줄의 발신자 이름은
	 * 계정명 그대로(담임 체리/담임 베리)라 cherishCoachWho 의 판정은 그걸로 한다 — 여기 표기와 별개다.
	 */
	/**
	 * 배틀 상대로 앉은 담임인가 (2026-09-14) — 랭킹봇은 「담임 」 접두 없이 실명(체리·베리·리치)으로 접속한다.
	 * 학교 코치 줄은 다음 말이 올 때까지 남지만(2026-09-05 결정), 이쪽은 판 내내 떠 있으면 어색해 5초 뒤 사라진다.
	 */
	cherishCoachTransient(name: string) {
		const n = name.replace(/^[^A-Za-z0-9\uAC00-\uD7A3]/, '').trim();
		return n === '체리' || n === '베리' || n === '리치' || n === '피치';
	}
	cherishCoachMeta(who: string) {
		if (who === 'lychee') return { name: '리치', img: '/cherish/ui/coach-lychee.png?v=1', emoji: '🍈' };
		if (who === 'peach') return { name: '피치', img: '/cherish/ui/coach-peach.png?v=1', emoji: '🍑' };
		return who === 'berry'
			? { name: '베리', img: '/cherish/ui/coach-berry.png?v=2', emoji: '🫐' }
			: { name: '체리', img: '/cherish/ui/coach-cherry.png?v=3', emoji: '🍒' };
	}
	cherishSchoolBattleEl(): JQuery | null {
		const $b: JQuery | undefined = (this.battle?.scene as any)?.$battle;
		return $b && $b.length ? $b : null;
	}
	cherishSchoolCard(html: string) {
		const div = document.createElement('div');
		div.innerHTML = html.replace(/<br\s*\/?>/g, '\n');
		const lines = (div.textContent || '').split('\n').map(s => s.trim()).filter(s => !!s);
		if (!lines.length) return;
		this.cherishSchoolCardSeen = true;
		this.cherishSchool = { lines, ended: !!this.battle?.ended, report: [], bubbleTimer: null, tries: 0 };
		if (!this.cherishSchool.ended) this.cherishSchoolRenderCard();
	}
	/** 학교 카드(교실·시험)가 온 판인가 — 그 판의 담임 말은 담임 노트(복기 기록)에 없으므로 종료 카드 안내를 안 띄운다 (2026-10-30). */
	cherishSchoolCardSeen = false;
	cherishSchoolRenderCard() {
		const s = this.cherishSchool;
		if (!s) return;
		const $b = this.cherishSchoolBattleEl();
		if (!$b) {
			// 배너는 방 로그 첫 묶음에 오는데 필드 DOM 은 렌더 뒤에 생긴다 — 잠깐 기다렸다 다시
			if (s.tries++ < 40) setTimeout(() => this.cherishSchoolRenderCard(), 250);
			return;
		}
		$b.children('.cherish-school-card, .cherish-school-cardbtn').remove();
		const title = s.lines[0];
		const $card = $('<div class="cherish-school-card"><div class="cherish-school-inner"></div></div>');
		const $in = $card.children();
		$in.append($('<h3></h3>').text(title));
		// 둘째 줄이 📖·✅ 접두 없이 오면 부제(과목 한 줄 요약) — 제목 바로 아래 크게 (2026-09-05)
		let rest = s.lines.slice(1);
		if (rest.length && !/^[📖✅]/.test(rest[0])) {
			$in.append($('<p class="cherish-school-subtitle"></p>').text(rest[0]));
			rest = rest.slice(1);
		}
		for (const l of rest) {
			const cls = l.startsWith('✅') ? 'todo' : l.startsWith('📖') ? 'concept' : 'note';
			$in.append($('<p></p>').addClass('cherish-school-' + cls).text(l));
		}
		const $ok = $('<button class="button cherish-school-ok">알겠어, 시작할게</button>');
		$ok.on('click', () => { $card.remove(); });
		$in.append($ok);
		$b.append($card);
		// 「📖 수업 카드」 다시 보기 알약은 2026-09-06 사용자 결정으로 제거 (수업 중 화면 잡동사니). 위 .remove() 는 옛 알약 청소용으로 남긴다.
	}
	cherishCoachSay(text: string, who: string = 'cherry', transient: boolean = false) {
		if (!this.cherishSchool) {
			// 카드 없이 담임 줄만 오는 경우(재접속 등) — 말풍선은 그대로 띄운다
			this.cherishSchool = { lines: [], ended: !!this.battle?.ended, report: [], bubbleTimer: null, tries: 0 };
		}
		const s = this.cherishSchool;
		// 배틀 뒤 판정도 말풍선 그대로 (성적표 모달 폐기). 말풍선은 자동으로 사라지지 않고 다음 말이 올 때까지 남는다
		// (2026-09-05 사용자 결정 — 예전 9초 자동 숨김은 팀프리뷰의 첫 인사가 수업 카드 모달 아래에서 떴다 사라져 못 봤다).
		const $b = this.cherishSchoolBattleEl();
		if (!$b) {
			// 필드 DOM 이 아직 없으면(입장 직후 로그 묶음) 잠깐 뒤 다시 — 첫 인사가 사라지지 않게
			if (s.tries++ < 40) setTimeout(() => this.cherishCoachSay(text, who, transient), 250);
			return;
		}
		let $bub = $b.children('.cherish-coach');
		if (!$bub.length) {
			$bub = $(
				'<div class="cherish-coach"><div class="cherish-coach-avatar">' +
				'<img alt="" onerror="this.style.display=\'none\'"><span></span></div>' +
				'<div class="cherish-coach-body"><div class="cherish-coach-name"></div>' +
				'<div class="cherish-coach-prev"></div><div class="cherish-coach-text"></div></div></div>'
			);
			$b.append($bub);
		}
		// 얼굴·이름은 말할 때마다 담임에 맞춘다 (복기 코치는 유저마다 담임이 다르다)
		const meta = this.cherishCoachMeta(who);
		const $img = $bub.find('.cherish-coach-avatar img');
		if ($img.attr('src') !== meta.img) $img.attr('src', meta.img).css('display', '');
		$bub.find('.cherish-coach-avatar span').text(meta.emoji);
		$bub.find('.cherish-coach-name').text(meta.name);
		$bub.toggleClass('cherish-coach-berry', who === 'berry');
		$bub.toggleClass('cherish-coach-lychee', who === 'lychee');
		$bub.toggleClass('cherish-coach-peach', who === 'peach');
		const prev = $bub.find('.cherish-coach-text').text();
		$bub.find('.cherish-coach-prev').text(prev);
		$bub.find('.cherish-coach-text').text(text);
		$bub.removeClass('cherish-coach-hide cherish-coach-pop');
		void ($bub[0] as HTMLElement).offsetWidth; // 애니메이션 재시작
		$bub.addClass('cherish-coach-pop');
		if (s.bubbleTimer) { clearTimeout(s.bubbleTimer); s.bubbleTimer = null; }
		// 담임 랭킹봇 인사·마무리만 5초 뒤 사라진다 (2026-09-14). 학교 코치 줄은 그대로 남는다 —
		// 옛 9초 자동 숨김은 팀프리뷰의 첫 인사가 수업 카드 모달 아래에서 떴다 사라져 못 봤다 (2026-09-05).
		// 다음 말이 오면 cherish-coach-hide 를 떼므로 마무리 줄은 다시 뜬다. CSS 가 0.45초 페이드를 준다.
		if (transient) s.bubbleTimer = window.setTimeout(() => { $bub.addClass('cherish-coach-hide'); }, 5000) as unknown as number;
	}
	/**
	 * 담임 줄 대기열 — 클라는 채팅을 도착 즉시 로그에 그리고(선점, instantAdd), 배틀 큐가 그 줄에 닿으면
	 * 「따라잡기」만 한다. 말풍선을 도착 시점에 띄우면 연출(기술 애니메이션)보다 먼저 떠서 「한 턴 늦게 뜬다」로
	 * 보였다 (2026-09-05 사용자 지적). 그래서 줄을 여기 담아 두고 battle.run() 의 선점 따라잡기 분기가
	 * 그 줄에 닿을 때 띄운다. 12초 안에 닿지 않으면(큐가 멈춘 경우) 그냥 띄운다 — 한 턴 연출이 길어야 8초 남짓이다.
	 */
	cherishCoachPending = new Map<string, { text: string, who: string, transient: boolean }>();
	/** 이 판에 담임이 한 말 수(인사 제외) — 종료 카드 「담임 노트 보기」 줄의 재료 (patch-coach-notes-tab.py, 2026-10-30). */
	cherishCoachCount = 0;
	cherishCoachQueue(rawLine: string, text: string, who: string = 'cherry', transient: boolean = false) {
		if (!transient) this.cherishCoachCount++;
		const b = this.battle as any;
		if (!b) { this.cherishCoachSay(text, who, transient); return; }
		// 담임 랭킹봇 인사는 **팀프리뷰(선봉 고르는 화면)**에서 온다 (2026-09-15). 그때 배틀 큐는 유저의 선택을
		// 기다리며 멈춰 있어서 큐에 맡기면 선봉을 고른 뒤에야 뜬다 — 판이 시작되기 전이면 바로 띄운다.
		// 판이 시작된 뒤(마무리 줄)는 기존대로 큐에 맞춘다: 마지막 기술 연출보다 먼저 뜨면 어색하다.
		if (transient && !b.turn) { this.cherishCoachSay(text, who, true); return; }
		if (!b.cherishCoachHook) {
			b.cherishCoachHook = (line: string) => {
				const t = this.cherishCoachPending.get(line);
				if (!t) return;
				this.cherishCoachPending.delete(line);
				this.cherishCoachSay(t.text, t.who, t.transient);
			};
		}
		this.cherishCoachPending.set(rawLine, { text, who, transient });
		window.setTimeout(() => {
			const t = this.cherishCoachPending.get(rawLine);
			if (!t) return;
			this.cherishCoachPending.delete(rawLine);
			this.cherishCoachSay(t.text, t.who, t.transient);
		}, 12000);
	}
	cherishSchoolEnd() {
		if (this.cherishSchool) this.cherishSchool.ended = true;
	}
	/**
	 * 졸업시험을 이긴 순간 체리쉬타워 안내 띠 (2026-09-24). 졸업생 15명이 한 번도 타워에 안 온 걸 보고 넣었다 —
	 * 홈이 「매칭하기」로 열려 타워 카드를 본 적이 없었다. 체리 말풍선은 건드리지 않고 필드 위에 따로 띄운다
	 * (사용자 결정: 체리 발화 말고 시스템 안내).
	 *
	 * 졸업 기록은 api 가 배틀 종료를 받고 남기므로 조금 기다렸다 한 번 묻는다(폴링 아님 — 이 순간 한 번, 늦으면 두 번 더).
	 * 이미 탑에 자리가 있으면(재시험) 안 띄운다. ⚠️ 「1층은 쉬워요」류 문구 금지 — 1층의 정체가 드러난다.
	 */
	cherishTowerNudged = false;
	cherishTowerNudge(winner: string) {
		if (this.cherishTowerNudged || !this.side) return;
		const s = this.cherishSchool;
		if (!s || !s.lines.some(l => l.includes('졸업시험'))) return;
		if (toID(winner) !== PS.user.userid) return;
		this.cherishTowerNudged = true;
		const check = (tries: number) => {
			fetch('https://cherish-world.com/api/showdown/tower', { credentials: 'include' })
				.then(r => r.json())
				.then(json => {
					const d = json?.ok ? json.data : null;
					const st = d?.status;
					if (!d || d.access === 'closed' || !st) return;
					if (!st.graduated) {
						// poll-ok: 주기가 아니다 — 졸업 기록이 늦게 남을 때를 위한 재시도 두 번뿐
						if (tries < 2) window.setTimeout(() => check(tries + 1), 3000);
						return;
					}
					if (st.floor == null) this.cherishTowerNudgeRender();
				})
				.catch(() => {});
		};
		window.setTimeout(() => check(0), 2500);
	}
	cherishTowerNudgeRender() {
		const $b = this.cherishSchoolBattleEl();
		if (!$b || $b.children('.cherish-tower-nudge').length) return;
		const $n = $(
			'<div class="cherish-tower-nudge"><span class="cherish-tower-nudge-ic" aria-hidden><i class="fa fa-cloud"></i></span>' +
			'<span class="cherish-tower-nudge-t"><b>체리쉬타워가 열렸어요.</b> 1층에서 이기면 탑에 이름이 올라가요.</span>' +
			'<button class="button cherish-tower-nudge-go">체리쉬타워로 가기</button></div>'
		);
		$n.find('.cherish-tower-nudge-go').on('click', () => {
			// 홈이 체리쉬타워 모드로 열리게 (patch-home-modes.py 의 첫 등반 규칙). 전에 고른 모드 기록을 지운다.
			try {
				localStorage.removeItem('cherish-home-mode-picked');
				localStorage.setItem('cherish-home-mode', 'tower');
			} catch {}
			// 홈을 먼저 열고 나서 닫는다 — 순서를 바꾸면 다른 방이 한 번 번쩍인다 (대회 판 「대진표로 돌아가기」와 같은 순서)
			PS.join('' as RoomID);
			PS.leave(this.id);
		});
		$b.append($n);
	}
	cherishSchoolRenderReport() {
		const s = this.cherishSchool;
		if (!s) return;
		const $b = this.cherishSchoolBattleEl();
		if (!$b) return;
		$b.children('.cherish-coach, .cherish-school-card').remove();
		let $r = $b.children('.cherish-school-report');
		if (!$r.length) {
			$r = $(
				'<div class="cherish-school-report"><div class="cherish-school-inner">' +
				'<div class="cherish-school-tag">🍒 담임 체리의 성적표</div><h3></h3>' +
				'<div class="cherish-school-lines"></div><button class="button cherish-school-ok">닫기</button></div></div>'
			);
			$r.find('.cherish-school-ok').on('click', () => { $r.remove(); });
			$b.append($r);
		}
		const head = s.report[0] || '';
		$r.find('h3').text(head);
		$r.toggleClass('cherish-school-pass', /통과|합격/.test(head));
		const $lines = $r.find('.cherish-school-lines').empty();
		for (const l of s.report.slice(1)) $lines.append($('<p></p>').text(l));
	}
	// cherish-school-coach:end
	// cherish-lesson-room:begin (patch-school-replay.py)
	/**
	 * 3교시 리플레이 수업 상태 (2026-09-06). null 이면 보통 배틀 방. 대본은 /cherish/ui/school-lesson<N>.json.
	 * phase: loading → playing → question → (wrong → wrong-explain → 되감기 → question) → scene-done → playing … → done | error
	 */
	cherishLesson: {
		data: any, sceneIndex: number, wrongCount: number, completed: boolean, error: string,
		phase: 'loading' | 'intro' | 'card' | 'playing' | 'question' | 'wrong' | 'wrong-explain' | 'scene-done' | 'done' | 'error',
		/** 담임 말에서 멈춰 「계속」 을 기다리는 중 (2026-09-06 사용자: 「글을 읽기도 전에 넘어간다」) */
		waitingCoach: boolean,
		/** 오답 뒤 되감는 중 — 정지 지점에 닿으면 질문 직전 담임 말을 다시 띄운다 (2026-09-06 사용자: 「아무 말 없이 되돌아간다」) */
		retrying: boolean,
	} | null = null;
	cherishLessonNo(): number {
		const fromArgs = Number((this.args as any)?.cherishLesson);
		if (fromArgs) return fromArgs;
		return Number(this.id.replace(/^battle-cherishlesson/, '')) || 3;
	}
	/** 배틀 엔진이 생긴 직후 한 번 (BattleDiv.componentDidMount · loadReplay). 대본을 받아 첫 장면을 튼다. */
	cherishLessonBoot(battle: any) {
		if (this.cherishLesson) return;
		const no = this.cherishLessonNo();
		this.cherishLesson = { data: null, sceneIndex: 0, wrongCount: 0, completed: false, error: '', phase: 'loading', waitingCoach: false, retrying: false };
		this.title = `체리의 포켓몬학교 ${no}교시`;
		battle.isReplay = false; // 큐가 비어도 「This replay ends here」 를 찍지 않는다 — 우리가 가지를 이어 붙인다
		battle.cherishLessonHook = (args: string[]) => this.cherishLessonLine(args);
		fetch(`/cherish/ui/school-lesson${no}.json?v=8`) // v=8: 2026-09-06 먼저 묶기 오답 대사 사용자 문구
			.then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
			.then(data => {
				if (!this.cherishLesson || !data?.scenes?.length) throw new Error('empty');
				this.cherishLesson.data = data;
				this.cherishLessonIntro();
			})
			.catch(() => {
				if (!this.cherishLesson) return;
				this.cherishLesson.phase = 'error';
				this.cherishLesson.error = '수업 대본을 불러오지 못했어요. 잠시 후 다시 열어 줘.';
				this.update(null);
			});
		this.update(null);
	}
	cherishLessonScene(): any {
		const L = this.cherishLesson;
		return L?.data?.scenes?.[L.sceneIndex] ?? null;
	}
	/**
	 * 시작 전 수업 카드 (2026-09-06 사용자: 「뭘 가르칠지·어떻게 보여 줄지·퀴즈를 맞혀야 한다는 걸 먼저 설명」) — 1·2교시와 같은 모달
	 * (cherishSchoolCard). 「알겠어, 시작할게」 를 누르면 첫 장면. 모달을 못 그리면 조작부의 「바로 시작」 이 대신한다.
	 */
	cherishLessonIntro() {
		const L = this.cherishLesson;
		if (!L) return;
		const lines: string[] = L.data?.intro || [];
		if (!lines.length) { this.cherishLessonStartScene(0); return; }
		L.phase = 'intro';
		this.cherishSchoolCard(lines.join('<br/>'));
		let tries = 0;
		const hook = () => {
			const $ok = this.cherishSchoolBattleEl()?.find('.cherish-school-ok');
			if ($ok && $ok.length) {
				$ok.on('click', () => { if (this.cherishLesson?.phase === 'intro') this.cherishLessonStartScene(0); });
				return;
			}
			if (tries++ < 40) setTimeout(hook, 250);
		};
		hook();
		this.update(null);
	}
	/** 장면 시작 — 엔진을 비우고 prefix(정지 표식까지)를 적재해 처음부터 재생. 표식에 닿으면 훅이 멈춘다. */
	cherishLessonStartScene(i: number) {
		const L = this.cherishLesson;
		const b = this.battle as any;
		if (!L || !b) return;
		const sc = L.data.scenes[i];
		if (!sc) return;
		this.cherishSchoolBattleEl()?.children('.cherish-school-card').remove(); // 도입 카드가 남아 있으면 치운다
		L.sceneIndex = i;
		L.waitingCoach = false;
		L.retrying = false;
		// 설명 카드 단계(로그 없음, 2026-09-06 「상태이상 한눈에」) — 모달로 띄우고 「알겠어, 시작할게」 뒤 다음 단계
		if (sc.card) {
			L.phase = 'card';
			b.pause();
			this.cherishSchoolCard((sc.card as string[]).join('<br/>'));
			let tries = 0;
			const hook = () => {
				const $ok = this.cherishSchoolBattleEl()?.find('.cherish-school-ok');
				if ($ok && $ok.length) {
					$ok.on('click', () => { if (this.cherishLesson?.phase === 'card' && this.cherishLesson.sceneIndex === i) this.cherishLessonStartScene(i + 1); });
					return;
				}
				if (tries++ < 40) setTimeout(hook, 250);
			};
			hook();
			this.update(null);
			return;
		}
		L.phase = 'playing';
		b.reset();
		b.stepQueue = [...sc.prefix];
		b.atQueueEnd = false;
		b.seekTurn(0);
		b.play();
		this.update(null);
	}
	/** 엔진 훅 — 담임 줄은 말풍선으로, 표식은 정지·종료로. true = 로그에 남기지 않는다. */
	cherishLessonLine(args: string[]): boolean {
		const L = this.cherishLesson;
		const b = this.battle as any;
		if (!L || !b) return false;
		if (args[0] === 'c' && this.cherishSchoolIsCoach(args[1] || '')) {
			if (b.seeking !== null) return true; // 되감기 빨리감기 중엔 담임 줄을 건너뛴다(옛 말풍선이 번쩍이지 않게)
			this.cherishCoachSay(args.slice(2).join('|'));
			// 담임 말에서 멈춘다 — 읽고 「계속」(또는 말풍선 클릭). 바로 뒤가 정지/종료 표식이면 그쪽이 멈추므로 여기선 안 멈춘다.
			let k = b.currentStep + 1;
			while (k < b.stepQueue.length && /^\|?$/.test(b.stepQueue[k])) k++;
			const next = b.stepQueue[k] || '';
			if (!next.startsWith('|cherishstop|') && !next.startsWith('|cherishend|')) {
				L.waitingCoach = true;
				b.pause();
				this.cherishLessonBindBubble();
				this.update(null);
			}
			return true;
		}
		if (args[0] === 'cherishstop') {
			L.phase = 'question';
			b.pause();
			if (L.retrying) {
				// 되감기로 돌아온 자리 — 빨리감기 중 건너뛴 질문 직전 담임 말을 다시 띄운다
				L.retrying = false;
				const sc = this.cherishLessonScene();
				const last = (sc?.prefix as string[] | undefined)?.filter((l) => l.startsWith('|c|')).pop();
				if (last) this.cherishCoachSay(last.split('|').slice(3).join('|'));
			}
			this.update(null);
			return true;
		}
		if (args[0] === 'cherishend') {
			if (args[1] === 'main') {
				const last = L.sceneIndex + 1 >= (L.data?.scenes?.length ?? 0);
				L.phase = last ? 'done' : 'scene-done';
				if (last) this.cherishLessonComplete();
			} else {
				L.phase = 'wrong-explain';
			}
			b.pause();
			this.update(null);
			return true;
		}
		return false;
	}
	/** 담임 말을 읽고 「계속」 — 재생 재개. 말풍선 클릭도 같은 동작. */
	cherishLessonContinue() {
		const L = this.cherishLesson;
		const b = this.battle as any;
		if (!L || !b || !L.waitingCoach) return;
		L.waitingCoach = false;
		this.cherishSchoolBattleEl()?.find('.cherish-coach').css({ cursor: '', 'pointer-events': '' });
		b.play();
		this.update(null);
	}
	/** 말풍선(.cherish-coach)을 눌러도 계속되게 — 말풍선은 살짝 늦게 그려질 수 있어 몇 번 재시도. */
	cherishLessonBindBubble(tries = 0) {
		const $c = this.cherishSchoolBattleEl()?.find('.cherish-coach');
		if ($c && $c.length) {
			// 말풍선은 필드 클릭을 막지 않게 pointer-events: none 이라(patch-school-coach.py) 대기 중에만 켠다
			$c.off('click.cherishlesson').on('click.cherishlesson', () => this.cherishLessonContinue()).css({ cursor: 'pointer', 'pointer-events': 'auto' });
			return;
		}
		if (tries < 12) setTimeout(() => this.cherishLessonBindBubble(tries + 1), 250);
	}
	/** 질문 카드에서 선택 — 고른 가지를 큐 뒤에 이어 붙이고 재생. */
	cherishLessonChoose(idx: number) {
		const L = this.cherishLesson;
		const sc = this.cherishLessonScene();
		const b = this.battle as any;
		if (!L || !sc || !b || L.phase !== 'question') return;
		const ch = sc.question?.choices?.[idx];
		if (!ch) return;
		const branch = ch.branch === 'main' ? 'main' : 'wrong';
		if (branch === 'wrong') L.wrongCount++;
		L.phase = branch === 'main' ? 'playing' : 'wrong';
		L.waitingCoach = false;
		L.retrying = false;
		b.stepQueue.push(...sc.branches[branch]);
		b.atQueueEnd = false;
		b.play();
		this.update(null);
	}
	/**
	 * 오답 설명 뒤 되감기 — 담임이 「다시 생각해 보자」 하고 잠깐 뒤 prefix 만 다시 적재해 정지 턴까지 빨리감기, 표식에서 다시 질문
	 * (정지 지점에 닿으면 질문 직전 담임 말을 다시 띄운다 — cherishLessonLine 의 retrying 분기).
	 */
	cherishLessonRetry() {
		const L = this.cherishLesson;
		const sc = this.cherishLessonScene();
		const b = this.battle as any;
		if (!L || !sc || !b || L.phase !== 'wrong-explain') return;
		L.phase = 'playing';
		L.waitingCoach = false;
		L.retrying = true;
		this.cherishCoachSay(L.data?.retry || '다시 생각해 보자. 아까 그 순간으로 돌아갈게.');
		this.update(null);
		setTimeout(() => {
			if (this.cherishLesson !== L || !L.retrying) return;
			b.reset();
			b.stepQueue = [...sc.prefix];
			b.atQueueEnd = false;
			b.seekTurn(sc.stopTurn);
			b.play();
			this.update(null);
		}, 1400);
	}
	cherishLessonNext() {
		const L = this.cherishLesson;
		if (!L) return;
		this.cherishLessonStartScene(L.sceneIndex + 1);
	}
	/** 끝까지 봤다 → api 가 진도(lessonN_at)를 찍는다. 오답 횟수는 통과에 영향 없음(사용자 결정). */
	cherishLessonComplete() {
		const L = this.cherishLesson;
		if (!L || L.completed) return;
		L.completed = true;
		fetch('https://cherish-world.com/api/showdown/school/replay-complete', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ lesson: this.cherishLessonNo() }),
		})
			.then(r => r.json())
			.then(json => {
				if (!json?.ok) {
					L.error = json?.message || '통과 기록을 남기지 못했어요. 홈에서 다시 확인해 줘.';
					this.update(null);
				}
				// 홈 카드는 fetchCherishHome 30초 폴링이 알아서 갱신한다
			})
			.catch(() => { L.error = '연결이 불안정해서 통과 기록을 못 남겼어요. 홈에서 다시 확인해 줘.'; this.update(null); });
	}
	cherishLessonClose() {
		const ps = PS as any;
		if (typeof ps.leave === 'function') ps.leave(this.id);
		else if (typeof ps.removeRoom === 'function') ps.removeRoom(this);
		ps.update?.();
	}
	// cherish-lesson-room:end
	override interruptClose(explicit?: boolean, elem?: HTMLElement | null) {
		if (this.isPlaying() || this.requireForfeit) {
			PS.join('forfeitbattle' as RoomID, { parentElem: elem, parentRoomid: this.id });
			return `You are still in ${this.title}`;
		}
		return super.interruptClose(explicit, elem);
	}
	isPlaying() {
		return this.battle && !this.battle.ended && this.request && this.connectMode !== 'deleted';
	}
	updateChoiceNotification() {
		let oName = this.battle?.farSide.name;
		if (oName) oName = " against " + oName;
		let title = '';
		let body = '';
		switch (this.request?.requestType) {
		case 'move':
			title = "내 차례예요!";
			body = "기술을 골라주세요" + oName;
			break;
		case 'switch':
			title = "교체할 차례예요!";
			body = "교체할 포켓몬을 골라주세요" + oName;
			break;
		case 'team':
			title = "선봉을 골라주세요!";
			body = "선봉 순서를 정해주세요" + oName;
			break;
		}

		if (!this.choices || this.choices.isDone()) body = '';

		const current = this.notifications.find(notification => notification.id === 'choice');
		if ((current?.body || '') === body) return;

		if (!body) {
			this.dismissNotification('choice');
		} else {
			this.notify({ title, body, id: 'choice', noAutoDismiss: true });
		}
	}

	override handleReconnect(): boolean | void {
		if (this.battle) {
			this.battle.stepQueue = [];
			this.battle.preemptStepQueue = [];
			this.battle.resetStep();
		}
		this.side = null;
		this.request = null;
		this.choices = null;
		this.updateChoiceNotification();
		return false;
	}

	override destroy() {
		this.request = null;
		this.choices = null;
		super.destroy();
	}

	loadReplay() {
		// cherish-lesson: loadReplay — 수업 방(battle-cherishlesson*)은 새로고침·직접 진입이어도 대본으로 튼다
		if (this.id.startsWith('battle-cherishlesson')) {
			if (this.battle) this.cherishLessonBoot(this.battle);
			this.connectMode = null;
			this.connectError = null;
			this.update(null);
			return;
		}
		const replayid = this.id.slice(7);
		Net(`https://replay.pokemonshowdown.com/${replayid}.json`).get().catch(() => '').then(data => {
			try {
				const replay = JSON.parse(data);
				this.title = `[${replay.format}] ${replay.players.join(' vs. ')}`;
				this.battle.stepQueue = replay.log.split('\n');
				this.battle.atQueueEnd = false;
				this.battle.pause();
				this.battle.seekTurn(0);
				this.connectMode = null;
				this.connectError = null;
				this.update(null);
			} catch {
				this.connectError = `Battle "${replayid}" not found`;
				if (!this.battle.stepQueue.length) {
					this.battle.scene.message(
						`<div class="broadcast-red pad"><strong>${BattleLog.escapeHTML(this.connectError)}</strong></div><br />` +
						`The battle you're looking for has expired. Battles expire after 15 minutes of inactivity unless they're saved.<br /><br />` +
						`In the future, remember to click "Save replay" to save a replay permanently.`
					);
				}
				this.update(null);
			}
		});
	}
}

class BattleDiv extends preact.Component<{ room: BattleRoom }> {
	override shouldComponentUpdate() {
		return false;
	}
	override componentDidMount() {
		const room = this.props.room;
		if (room.battle) {
			this.base!.replaceChild(room.battle.scene.$frame![0], this.base!.firstChild!);
		}
	}
	override render() {
		return <div><div class="battle"></div></div>;
	}
}

class TimerButton extends preact.Component<{ room: BattleRoom, top: number }> {
	timerInterval: number | null = null;
	override componentWillUnmount() {
		this.cherishRemoveHud(); // cherish-timer-hud
		if (this.timerInterval) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}
	}
	// cherish-timer-hud:begin (patch-battle-timer-hud.py)
	/**
	 * 배틀 필드 안 남은 시간 HUD. 이 버튼은 조작부(필드 밖)에 있어 싸우는 동안 눈이 안 가므로
	 * 같은 값을 「N턴」 표시 아래에 한 번 더 그린다. 카운트다운·상태는 전부 이 컴포넌트가 이미
	 * 갖고 있으니 렌더 결과를 DOM 에 옮겨 적기만 한다(별도 타이머 없음).
	 */
	cherishSyncHud(main: string, bank: string, state: '' | 'on' | 'critical') {
		const $battle: JQuery | undefined = (this.props.room.battle?.scene as any)?.$battle;
		if (!$battle) return;
		let $hud = $battle.children('.cherish-timerhud');
		if (!$hud.length) {
			$hud = $('<div class="cherish-timerhud"><i class="fa fa-hourglass-start" aria-hidden="true"></i> <span></span><small></small></div>');
			$battle.append($hud);
		}
		$hud.children('span').text(main);
		$hud.children('small').text(bank);
		$hud.toggleClass('cherish-timerhud-on', state === 'on');
		$hud.toggleClass('cherish-timerhud-critical', state === 'critical');
	}
	cherishRemoveHud() {
		const $battle: JQuery | undefined = (this.props.room.battle?.scene as any)?.$battle;
		$battle?.children('.cherish-timerhud').remove();
	}
	// cherish-timer-hud:end
	secondsToTime(seconds: number | true) {
		if (seconds === true) return '-:--';
		const minutes = Math.floor(seconds / 60);
		seconds -= minutes * 60;
		return `${minutes}:${(seconds < 10 ? '0' : '')}${seconds}`;
	}
	render() {
		let time = '타이머';
		const room = this.props.room;
		if (!this.timerInterval && room.battle.kickingInactive) {
			this.timerInterval = setInterval(() => {
				if (room.choices?.isDone()) return;
				if (typeof room.battle.kickingInactive === 'number' && room.battle.kickingInactive > 1) {
					room.battle.kickingInactive--;
					if (room.battle.graceTimeLeft) room.battle.graceTimeLeft--;
					else if (room.battle.totalTimeLeft) room.battle.totalTimeLeft--;
				}
				this.forceUpdate();
			}, 1000);
		} else if (this.timerInterval && !room.battle.kickingInactive) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}

		let timerTicking = (room.battle.kickingInactive &&
			room.request && room.request.requestType !== "wait" && (room.choices && !room.choices.isDone())) ?
			' timerbutton-on' : '';

		if (room.battle.kickingInactive) {
			const secondsLeft = room.battle.kickingInactive;
			time = this.secondsToTime(secondsLeft);
			if (secondsLeft !== true) {
				if (secondsLeft <= 10 && timerTicking) {
					timerTicking = ' timerbutton-critical';
				}

				if (room.battle.totalTimeLeft) {
					const totalTime = this.secondsToTime(room.battle.totalTimeLeft);
					time += ` |  ${totalTime} total`;
				}
			}
		}

		// cherish-timer-hud: 버튼과 같은 값·같은 상태를 필드 안에도 그린다
		if (room.battle.kickingInactive) {
			const hudMain = this.secondsToTime(room.battle.kickingInactive);
			const hudBank = room.battle.totalTimeLeft ? `/ ${this.secondsToTime(room.battle.totalTimeLeft)}` : '';
			const hudState = timerTicking === ' timerbutton-critical' ? 'critical' : timerTicking ? 'on' : '';
			this.cherishSyncHud(hudMain, hudBank, hudState);
		} else {
			this.cherishRemoveHud();
		}

		return <button
			style={{ position: "absolute", right: '10px', top: `${this.props.top}px` }}
			data-href="battletimer" class={`button${timerTicking}`} role="timer"
		>
			<i class="fa fa-hourglass-start" aria-hidden></i> {time}
		</button>;
	}
};

class BattlePanel extends PSRoomPanel<BattleRoom> {
	static readonly id = 'battle';
	static readonly routes = ['battle-*', 'game-*'];
	static readonly Model = BattleRoom;
	static handleDrop(ev: DragEvent) {
		const file = ev.dataTransfer?.files?.[0];
		if (file?.type === 'text/html') {
			let roomNum = 1;
			for (; roomNum < 100; roomNum++) {
				if (!PS.rooms[`battle-uploaded-${roomNum}`]) break;
			}
			file.text().then(html => {
				const titleStart = html.indexOf('<title>');
				const titleEnd = html.indexOf('</title>');
				let title = 'Uploaded Replay';
				if (titleStart >= 0 && titleEnd > titleStart) {
					title = html.slice(titleStart + 7, titleEnd - 1);
					const colonIndex = title.indexOf(':');
					const hyphenIndex = title.lastIndexOf('-');
					if (hyphenIndex > colonIndex + 2) {
						title = title.substring(colonIndex + 2, hyphenIndex - 1);
					} else {
						title = title.substring(colonIndex + 2);
					}
				}
				const index1 = html.indexOf('<script type="text/plain" class="battle-log-data">');
				const index2 = html.indexOf('<script type="text/plain" class="log">');
				if (index1 < 0 && index2 < 0) {
					PS.alert("Unrecognized HTML file: Only replay files are supported.");
					return;
				}
				if (index1 >= 0) {
					html = html.slice(index1 + 50);
				} else if (index2 >= 0) {
					html = html.slice(index2 + 38);
				}
				const index3 = html.indexOf('</script>');
				html = html.slice(0, index3);
				html = html.replace(/\\\//g, '/');

				PS.join(`battle-uploaded-${roomNum}` as RoomID);
				const room = PS.rooms[`battle-uploaded-${roomNum}`] as BattleRoom;
				if (!room) return;

				room.title = title;
				room.connectMode = null;
				PS.receive(`>battle-uploaded-${roomNum}\n${html}`);
			});
			return true;
		}
	}
	/** last displayed team. will not show the most recent request until the last one is gone. */
	team: ServerPokemon[] | null = null;
	mobileChatShown = false;
	showMobileChat = () => {
		this.mobileChatShown = true;
		this.forceUpdate();
	};
	showMobileBattle = () => {
		this.mobileChatShown = false;
		this.forceUpdate();
	};
	send = (text: string, elem?: HTMLElement) => {
		this.props.room.send(text, elem);
	};
	focusIfNoSelection = () => {
		if (window.getSelection?.()?.type === 'Range') return;
		this.focus();
	};
	onKey = (e: KeyboardEvent) => {
		if (e.keyCode === 33) { // Pg Up key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop - chatLog.offsetHeight + 60;
			return true;
		} else if (e.keyCode === 34) { // Pg Dn key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop + chatLog.offsetHeight - 60;
			return true;
		}
		return false;
	};
	toggleBoostedMove = (e: Event) => {
		const checkbox = e.currentTarget as HTMLInputElement;
		const choices = this.props.room.choices;
		if (!choices) return; // shouldn't happen
		switch (checkbox.name) {
		case 'mega':
			choices.current.mega = checkbox.checked;
			break;
		case 'megax':
			choices.current.megax = checkbox.checked;
			choices.current.megay = false;
			break;
		case 'megay':
			choices.current.megay = checkbox.checked;
			choices.current.megax = false;
			break;
		case 'ultra':
			choices.current.ultra = checkbox.checked;
			break;
		case 'z':
			choices.current.z = checkbox.checked;
			break;
		case 'max':
			choices.current.max = checkbox.checked;
			break;
		case 'tera':
			choices.current.tera = checkbox.checked;
			break;
		}
		this.props.room.update(null);
	};
	override componentDidMount() {
		const room = this.props.room;
		const $elem = $(this.base!);
		const battle = (room.battle ||= new Battle({
			id: room.id as any,
			$frame: $elem.find('.battle'),
			$logFrame: $elem.find('.battle-log'),
			log: room.backlog?.map(args => '|' + args.join('|')),
		}));
		const scene = battle.scene as BattleScene;
		// cherish-backlog-replay:begin (patch-cherish-bgm.py)
		// 따라잡기(관전·새로고침)로 받은 줄은 receiveLine 을 안 거치고 Battle 의 log 로만 들어간다.
		// 우리 줄(|cherishbgm|·|cherishform|·|cherishclass|)은 room.battle 의 맵에 적는 처리기라 여기서 되돌리면 산다 —
		// seekTurn 이 |start|·|switch| 에 닿을 때 rollBgm·addPokemon·트레이너 그림이 그 맵을 읽는다.
		for (const cherishArgs of room.backlog || []) {
			if (typeof cherishArgs[0] === 'string' && cherishArgs[0].startsWith('cherish')) {
				try { this.receiveLine(cherishArgs); } catch {}
			}
		}
		// cherish-backlog-replay:end
		room.backlog = null;
		room.log ||= scene.log;
		room.log.getHighlight = room.handleHighlight;
		scene.tooltips.unlisten(scene.$frame);
		scene.tooltips.listen(this.base!);
		super.componentDidMount();
		battle.seekTurn(Infinity); // cherish-playback-removed: 입장 시 최신 턴
		battle.play();
		if (PS.prefs.autohardcore) {
			battle.setHardcoreMode(true);
		}
		// cherish-extraction-completion:begin
		let cherishExtractionNotified = false;
		const cherishNotifyExtractionComplete = () => {
			if (!battle.ended || !battle.atQueueEnd || !room.id.startsWith('battle-gen9cherishextraction-') ||
				window.name !== 'cherish-extraction' || window.parent === window) return;
			// Playback queue completion occurs after the last attack/faint animation.
			Object(scene).bgm?.pause();
			if (cherishExtractionNotified) return;
			cherishExtractionNotified = true;
			window.parent.postMessage({type: 'cherish:extraction-animation-complete', roomid: room.id}, 'https://cherish-world.com');
		};
		battle.subscribe((state) => {
			this.forceUpdate();
			if (state === 'atqueueend') cherishNotifyExtractionComplete();
		});
		cherishNotifyExtractionComplete(); // Rejoining an already finished battle.
		// cherish-extraction-completion:end
		if ((room as any).args?.cherishLesson || room.id.startsWith('battle-cherishlesson')) (room as any).cherishLessonBoot(battle); // cherish-lesson: boot
	}
	override componentWillUnmount() {
		const scene = this.props.room.battle?.scene as BattleScene | undefined;
		if (this.base) scene?.tooltips.unlisten(this.base);
		super.componentWillUnmount();
	}
	battleHeight = 360;
	updateLayout() {
		if (!this.base) return;
		const room = this.props.room;
		if (!room.width) return;
		const { battleHeight } = this.chooseLayout();
		this.battleHeight = battleHeight;
		if (battleHeight !== 360) {
			room.battle?.scene.$frame!.css('transform', `scale(${battleHeight / 360})`);
		} else {
			room.battle?.scene.$frame!.css('transform', 'none');
		}
	}
	chooseLayout(): {
		layout: BattlePanelLayout,
		battleHeight: number,
		battleWidth: number,
		overlayControls: boolean,
	} {
		const room = this.props.room;
		// cherish-bshell: 단일 레이아웃 — 필드는 폭에 맞추되 높이의 52% · 1.25배를 넘지 않는다 (조작부가 스크롤 없이 들어가게)
		if (BattlePanel.cherishShellOn()) {
			const pad = room.width > 700 ? 28 : 16;
			const scale = Math.max(0.3, Math.min((room.width - pad) / 640, (room.height * 0.52) / 360, 1.25));
			return { layout: 'top-and-bottom', battleHeight: Math.round(360 * scale), battleWidth: Math.round(640 * scale), overlayControls: false };
		}
		return PS.chooseBattleLayout(room.width, room.height, PS.prefs.battlelayout);
	}
	fastForwardIfRejoining() {
		const room = this.props.room;
		if (!room.rejoining || !room.side) return;
		room.rejoining = false;
		room.battle.seekTurn(Infinity);
	}
	override receiveLine(args: Args) {
		const room = this.props.room;
		// cherish-school-coach: 학교 판 표면 재료 (patch-school-coach.py) — 로그엔 그대로 남긴다
		if (args[0] === 'uhtml' && args[1] === 'cherishdash' && (args[2] || '').includes('체리의 포켓몬학교')) room.cherishSchoolCard(args[2]);
		// 담임 줄은 즉시 띄우지 않고 배틀 큐가 이 줄에 닿을 때(연출 동기) 띄운다 — cherishCoachQueue 참고
		if (args[0] === 'c' && room.cherishSchoolIsCoach(args[1] || '')) room.cherishCoachQueue('|' + args.join('|'), args.slice(2).join('|'), room.cherishCoachWho(args[1] || ''), room.cherishCoachTransient(args[1] || ''));
		if (args[0] === 'c:' && room.cherishSchoolIsCoach(args[2] || '')) room.cherishCoachQueue('|' + args.join('|'), args.slice(3).join('|'), room.cherishCoachWho(args[2] || ''), room.cherishCoachTransient(args[2] || ''));
		if (args[0] === 'win' || args[0] === 'tie') room.cherishSchoolEnd();
		if (args[0] === 'win') room.cherishTowerNudge(args[1] || '');
		if (BattlePanel.cherishShellOn()) this.cherishShellLine(args); // cherish-bshell: 토스트·종료 카드 재료
		switch (args[0]) {
		// cherish-party-owners:begin (patch-party-control.py)
		// 파티 난투전 — 팀 3마리의 개체별 주인. 슬롯을 나눠 쓰는 두 사람에게만 온다.
		// 소풍(익스트랙션) 협동의 개체별 조작 권한·대기 표시 두 case 도 블록 주인인 여기가 든다 (2026-10-17, 09-19 규약).
		// 예전엔 patch-extraction-coop.py 가 이 블록 안에 끼워 넣어서 이 패치를 다시 바르면 사라졌다(verify FOREIGN).
		// ⚠️ 글자를 저쪽과 **똑같이** 두고, 두 case 를 cherishowners case 바로 위에 붙여 둘 것 — 저쪽은
		// 「두 case + cherishowners 줄」 덩어리가 이미 있으면 아무것도 안 한다. 어긋나면 case 가 두 벌 들어간다.
		case 'extractioncontrols':
			try { (room as any).cherishExtractionOwners = JSON.parse(args[1]); } catch {}
			this.forceUpdate(); return;
		case 'extractionpending':
			try { (room as any).cherishExtractionPending = JSON.parse(args[1]); } catch {}
			this.forceUpdate(); return;
		case 'cherishowners': {
			try {
				const data = JSON.parse(args[1]);
				(room as any).cherishOwners = data.owners || null;
				(room as any).cherishOwnerNames = data.names || {};
				// 이름 맵은 request 를 받은 뒤에 굳힌다 (아래 receiveRequest 참고)
				(room as any).cherishOwnerByName = null;
			} catch {}
			return;
		}
		// cherish-party-owners:end
		// cherish-bgm-proto:begin (patch-cherish-bgm.py)
		case 'cherishbgm': {
			// 이 방에서 틀 곡 (2026-09-09 저녁). 서버가 안 보내면 이 줄 자체가 안 오고,
			// 클라는 방 번호 해시로 기본곡을 튼다 (battle-animations.ts rollBgm).
			const cherishTrack = { id: args[1] || '', owner: args[2] || null };
			const cherishBattle = room.battle as any;
			if (cherishBattle && cherishTrack.id) {
				// rollBgm 이 turn 0 에서 이 값을 읽는다 (보통 이 줄이 먼저 온다 = 왕복 0).
				cherishBattle.cherishBgmTrack = cherishTrack;
				// 이미 음악이 시작된 뒤에 도착했으면(훅 응답이 늦은 판) 그 자리에서 갈아탄다.
				cherishBattle.scene?.cherishApplyTrack?.(cherishTrack.id, cherishTrack.owner);
			}
			return;
		}
		// cherish-bgm-proto:end
		// cherish-form-proto:begin (patch-custom-form.py)
		case 'cherishform': {
			// 개체 전용 커스텀 폼 (2026-09-10). `|cherishform|<side>|<slot>|<formId>`.
			// 서버가 방 로그로 내려주므로 관전자·새로고침·리플레이도 같은 줄을 받는다.
			const cherishFormSide = args[1] || '';
			const cherishFormSlot = Number(args[2]);
			const cherishFormName = args[3] || '';
			const cherishFormBattle = room.battle as any;
			if (cherishFormBattle && cherishFormSide && cherishFormName && Number.isInteger(cherishFormSlot)) {
				if (!cherishFormBattle.cherishForms) cherishFormBattle.cherishForms = {};
				if (!cherishFormBattle.cherishForms[cherishFormSide]) cherishFormBattle.cherishForms[cherishFormSide] = {};
				cherishFormBattle.cherishForms[cherishFormSide][cherishFormSlot] = cherishFormName;
				// 보통은 `|start|` 보다 먼저 도착한다(cherish-form-late). 훅 응답이 늦어 그 자리 개체가 이미
				// 만들어졌으면 스프라이트만 다시 만든다 — 무대 밖 개체만. 이미 무대 위인 아이는 이번 판엔 보통
				// 그림으로 둔다(갈아 끼우면 위치·투명도를 새로 잡아야 한다). 포맷 전부 팀 미리보기가 있어
				// 이 줄은 사실상 `|start|` 앞에 온다. 메서드가 없으면 조용히 넘어간다.
				const cherishFormMon = cherishFormBattle.getSide?.(cherishFormSide)?.pokemon?.[cherishFormSlot];
				if (cherishFormMon && !cherishFormMon.isActive?.()) cherishFormMon.side?.cherishRefreshFormSprite?.(cherishFormMon);
			}
			return;
		}
		// cherish-form-proto:end
		// cherish-blossom-class:begin (patch-class-blossom.py)
		case 'cherishclass': {
			// 반 표식 (2026-09-14). `|cherishclass|<discordUserId>|<반 꽃 이모지>`.
			const cherishClsId = args[1] || '';
			const cherishClsEmoji = args[2] || '';
			const cherishClsBattle = room.battle as any;
			if (cherishClsBattle && cherishClsId && cherishClsEmoji) {
				if (!cherishClsBattle.cherishClassBadges) cherishClsBattle.cherishClassBadges = {};
				cherishClsBattle.cherishClassBadges[cherishClsId] = cherishClsEmoji;
				// 보통은 |start| 보다 먼저 온다. 늦게 오면 이미 그려진 트레이너 줄을 다시 그린다.
				if (cherishClsBattle.started) cherishClsBattle.scene?.resetSides?.();
			}
			return;
		}
		// cherish-blossom-class:end
		case 'cherishresult': { // cherish-bshell: 종료 카드 수치 (이 유저에게만 온다)
			try { (room as any).cherishResult = JSON.parse(args[1]); } catch {}
			room.update(null);
			return;
		}
		// cherish-team-chat-recv:begin (patch-team-chat-ui.py)
		case 'cherishteamchat': { // 같은 편에게만 sendTo 로 온다 (server/cherish-team-chat.ts)
			try { cherishTeamChatReceive(room, JSON.parse(args[1])); } catch {}
			room.update(null);
			return;
		}
		// cherish-team-chat-recv:end
		// cherish-speed-memo-recv:begin (patch-speed-memo-ui.py)
		// 속도 기억 — 그 좌석 사람에게만 온다. 받으면 지금 선 아이들의 상태바를 다시 그린다.
		case 'cherishspeed': {
			try {
				const battle = (room as any).battle;
				if (battle) {
					battle.cherishSpeedMemo = JSON.parse(args.slice(1).join('|'));
					for (const side of battle.sides || []) {
						for (const mon of side.active || []) if (mon) mon.sprite?.updateStatbarIfExists?.(mon);
					}
				}
			} catch {}
			return;
		}
		// cherish-speed-memo-recv:end
		case 'cantleave':
			room.requireForfeit = true;
			return;
		case 'allowleave':
			room.requireForfeit = false;
			return;
		case 'initdone':
			room.battle.seekTurn(Infinity); // cherish-playback-removed: 입장 시 최신 턴
			room.battle.play();
			return;
		case 'request':
			this.receiveRequest(args[1] ? JSON.parse(args[1]) : null);
			return;
		case 'win': case 'tie':
			this.receiveRequest(null);
			break;
		case 'c': case 'c:': case 'chat': case 'chatmsg': case 'inactive':
			room.battle.instantAdd('|' + args.join('|'));
			return;
		case 'error':
			if (args[1].startsWith('[Invalid choice]') && room.request) {
				room.choices = new BattleChoiceBuilder(room.request);
				room.updateChoiceNotification();
				room.update(null);
			}
			break;
		case 'sentchoice':
			if (room.request) {
				let choices = new BattleChoiceBuilder(room.request);
				const possibleError = choices.addChoices(args[1]);
				if (possibleError || !choices.isDone()) {
					choices = new BattleChoiceBuilder(room.request);
					choices.serializedChoice = args[1];
				}
				room.choices = choices;
			}
			room.updateChoiceNotification();
			room.update(null);
			return;
		}
		room.battle.add('|' + args.join('|'));
		if (PS.prefs.noanim) this.props.room.battle.seekTurn(Infinity);
	}
	receiveRequest(request: BattleRequest | null) {
		const room = this.props.room;
		if (!request) {
			room.request = null;
			room.choices = null;
			room.updateChoiceNotification();
			return;
		}

		if (PS.prefs.autotimer && !room.battle.kickingInactive && !room.autoTimerActivated) {
			this.send('/timer on');
			room.autoTimerActivated = true;
		}

		BattleChoiceBuilder.fixRequest(request, room.battle);

		if (request.side) {
			const wasPlayer = !!room.side;
			room.battle.myPokemon = request.side.pokemon;
			room.battle.setViewpoint(request.side.id);
			room.side = request.side;
			if (!wasPlayer) this.fastForwardIfRejoining();
		}
		if (request.ally) {
			room.battle.myAllyPokemon = request.ally.pokemon;
		}

		room.request = request;
		room.choices = new BattleChoiceBuilder(request);
		// A reconnect can send `|sentchoice|` immediately after `|request|`.
		// Wait until the entire protocol message has been processed before notifying.
		Promise.resolve().then(() => room.updateChoiceNotification());
		room.update(null);
	}
	renderConnectError() {
		const room = this.props.room;
		// cherish-reconnect-strip:begin (patch-reconnect-strip.py)
		// 끊김 표시 (2026-09-24). 업스트림 표시는 로그 줄·채팅 패널뿐이라 배틀 셸에서 안 보였다 —
		// 누른 기술이 한참 반응 없다가 들어가니 유저가 다시 누르거나 새로고침했다.
		// cherishHeld 는 patch-reconnect-hold.py 가 붙잡아 둔 선택이다(없으면 둘째 줄만 빠진다).
		const cherishHeldHere = ((PS.connection as any)?.cherishHeld || []).some((h: any) => h.roomid === room.id);
		if (PS.isOffline || room.connectMode === 'pending-reconnect' || cherishHeldHere) {
			return <div class="cw-reconnect" role="status" aria-live="polite">
				<i class="fa fa-refresh fa-spin cw-reconnect-icon" aria-hidden></i>
				<span class="cw-reconnect-text">
					<strong>연결이 잠깐 끊겼어요. 다시 잇는 중이에요</strong>
					{cherishHeldHere && <small>방금 누른 건 연결되면 바로 들어가요.</small>}
				</span>
				{PS.isOffline && <button class="button cw-reconnect-btn" data-cmd="/reconnect">지금 연결</button>}
			</div>;
		}
		// cherish-reconnect-strip:end
		if (room.connectMode !== 'deleted' && room.connectMode !== 'not-found') {
			return null;
		}
		return <div class="pad"><div class="broadcast-red pad">
			<h3>{room.connectError || "오류"}</h3>
			<p class="buttonbar"><button class="button" data-cmd="/close"><strong>닫기</strong></button></p>
		</div></div>;
	}
	// cherish-party-control:begin (patch-party-control.py)
	/**
	 * 이름 → 주인 맵을 굳힌다. **첫 request 때 한 번만** 만든다.
	 *
	 * owners 는 팀 순서인데 쇼다운은 교체가 일어나면 request.side.pokemon 을 재배열한다
	 * (활성이 앞으로). 인덱스로 계속 대조하면 다음 턴부터 주인이 어긋나므로, 순서가 아직
	 * 원본 그대로인 첫 request 에서 이름으로 옮겨 담는다.
	 */
	cherishFreezeOwners() {
		const room = this.props.room as any;
		if (room.cherishOwnerByName || !room.cherishOwners) return;
		const mons = room.request?.side?.pokemon;
		if (!mons?.length) return;
		// ⚠️ 배틀 도중에 들어온 request 는 **순서를 믿을 수 없다** (2026-09-09 실측).
		// 새로고침하면 첫 request 가 이미 교체가 반영된 순서로 와서, 이름 → 주인 지도가 통째로 어긋난다.
		// 그러면 양쪽 다 「파트너 차례」로 보여 **아무도 조작을 못 한다**(실제로 그렇게 잠겼다).
		// 순서를 믿을 수 없으면 지도를 비워 둔다 → 주인 판정이 없어져 **둘 다 조작 가능**으로 떨어진다.
		// 조작 차단은 원래 「누가 지시할 차례인지」 헷갈리지 않게 하는 UX 장치이지 규칙이 아니다
		// (같은 팀이라 남의 아이를 눌러도 이득이 없다) — 잠기는 것보다 열리는 쪽이 옳은 실패다.
		if ((room.battle?.turn ?? 0) > 1) { room.cherishOwnerByName = {}; return; }
		const map: { [name: string]: string } = {};
		mons.forEach((mon: any, i: number) => {
			const ident: string = mon?.ident || '';
			const name = ident.slice(ident.indexOf(': ') + 2);
			if (name && room.cherishOwners[i]) map[name] = room.cherishOwners[i];
		});
		room.cherishOwnerByName = map;
	}
	/**
	 * `|cherishowners|`(배틀 스트림) 기반 판정 — 그 이름의 주인이 내가 아니면 표시명.
	 *
	 * 난투전의 원래 길이자, 미궁에서는 **api 응답이 오기 전의 임시 답**이다. 굳히기가
	 * turn > 1 이면 스스로 지도를 비우므로(새로고침 재접속) 「못 믿으면 열어 둔다」는 그대로다.
	 */
	cherishSeatOwnerTurn(activeName: string): string | null {
		const room = this.props.room as any;
		if (!room.cherishOwners) return null;
		this.cherishFreezeOwners();
		const owner = activeName ? room.cherishOwnerByName?.[activeName] : null;
		if (!owner || owner === PS.user.userid) return null;
		return room.cherishOwnerNames?.[owner] || owner;
	}
	/** 미궁에서는 현재 방의 API 이름 지도로만 권한을 판정한다. */
	cherishMazeOwners(room: any) {
		if (!String(room.id || '').startsWith('battle-gen9cherishmaze-')) return null;
		const viewer = PS.user.userid;
		if (room.cherishMazeViewer !== viewer) {
			room.cherishMazeViewer = viewer;
			room.cherishMazeMine = null;
			room.cherishMazeTrust = false;
			room.cherishMazeRetryAt = 0;
			room.cherishMazeTries = 0;
		}
		if (room.cherishMazeMine || room.cherishMazeFetching || Date.now() < (room.cherishMazeRetryAt || 0)) return room.cherishMazeMine;
		room.cherishMazeFetching = true;
		const controller = new AbortController();
		const timeout = setTimeout(() => controller.abort(), 8000);
		fetch('https://cherish-world.com/api/showdown/maze', { credentials: 'include', cache: 'no-store', signal: controller.signal })
			.then(res => res.json())
			.then(json => {
				if (PS.user.userid !== viewer) return;
				const run = json && json.ok ? json.data?.status?.run : null;
				// 다른 단계/런의 응답이나 전투 생성 전 응답을 현재 방에 영구 캐시하지 않는다.
				if (!run || run.ongoingRoom !== room.id || !Array.isArray(run.team)) return;
				if (!run.party) { room.cherishMazeMine = {}; return; }
				const mine: { [name: string]: boolean } = Object.create(null);
				let owned = 0;
				for (const p of run.team) {
					if (!p || typeof p.name !== 'string' || typeof p.mine !== 'boolean' || p.name in mine) return;
					mine[p.name] = p.mine;
					if (p.mine) owned++;
				}
				if (!owned || owned === run.team.length) return;
				room.cherishMazeMine = mine;
				room.cherishMazeTrust = true;
				room.cherishMazeMate = run.party.withName || '동행자';
			})
			.catch(() => {})
			.then(() => {
				clearTimeout(timeout);
				room.cherishMazeFetching = false;
				// ⚠️ 첫 조회는 **구조적으로 빗나간다** — api 가 방을 먼저 만들고 배틀 행을 그 뒤에 넣으므로
				// (pokemon-maze.ts openMazeBattle) 방이 열린 직후엔 ongoingRoom 이 아직 비어 있다.
				// 5초 고정이면 그 한 번을 5초로 갚는다. 처음 두 번만 짧게 되묻는다.
				const tries = (room.cherishMazeTries = (room.cherishMazeTries || 0) + 1);
				const wait = tries <= 1 ? 1000 : tries === 2 ? 2000 : 5000; // poll-ok: 방 하나당 한 통로
				room.cherishMazeRetryAt = Date.now() + wait;
				if (!this.base?.isConnected || room.battle?.ended) return;
				this.forceUpdate();
				if (!room.cherishMazeMine) setTimeout(() => {
					if (this.base?.isConnected && !room.battle?.ended) this.forceUpdate();
				}, wait);
			});
		return room.cherishMazeMine;
	}
	/** 지금 지시할 사람이 내가 아니면 그 사람의 표시명, 아니면 null. */
	cherishOtherTurn(): string | null {
		const room = this.props.room as any;
		const mazeMine = this.cherishMazeOwners(room);
		// 미궁 조회 지연/실패 시 순서 기반 공용 지도로 돌아가면 양쪽이 잠길 수 있다.
		if (String(room.id || "").startsWith("battle-gen9cherishmaze-")) {
			const active = room.request?.side?.pokemon?.find((p: any) => p?.active);
			const ident = active?.ident || "";
			const name = ident.includes(": ") ? ident.slice(ident.indexOf(": ") + 2) : "";
			if (room.cherishMazeTrust) return mazeMine?.[name] === false ? (room.cherishMazeMate || "동행자") : null;
			// ⚠️ api 응답 전에 그냥 열어 두면 **그 라운드 주인이 아닌 쪽에게 조작부가 통째로 뜬다**
			// (2026-09-22 신고 — 동행자 화면에 파트너 아이의 기술 네 개가 몇 초간 떴다). 그 몇 초는
			// 그냥 헷갈리는 게 아니라 실제로 눌린다 — 서버는 같은 슬롯에 탄 동승자의 choose 를 받는다.
			// 정작 답은 이미 와 있다: 미궁 방도 `|cherishowners|` 를 받고(patch-maze-party.py →
			// cherishSetOwners / cherishAttachAlly) 개체별 주인은 /team 이 단계마다 실어 준다.
			// 그래서 api 지도가 오기 전까지는 그 자리 지도로 가린다. 새로고침 재접속이면 굳히기가
			// 스스로 비어(turn > 1) null 로 떨어지므로 「못 믿으면 열어 둔다」는 그대로 산다.
			return this.cherishSeatOwnerTurn(name);
		}
		const active = room.request?.side?.pokemon?.find((mon: any) => mon?.active && !mon?.condition?.endsWith(' fnt'));
		const ident: string = active?.ident || '';
		return this.cherishSeatOwnerTurn(active ? ident.slice(ident.indexOf(': ') + 2) : '');
	}
	renderCherishOtherTurn(who: string) {
		return <div class="controls">
			<p style="text-align:center;padding:10px 0">
				{/* ⚠️ 강제 교체 때도 이 화면이 뜬다 — 그때는 필드가 비어 있으니 「나와 있어요」로 쓰면 안 맞는다 */}
				<strong>{who}</strong> 님이 지시할 차례예요.<br />
				<small>내 아이 차례가 오면 다시 고를 수 있어요!</small>
			</p>
		</div>;
	}
	// cherish-party-control:end
	// cherish-blossom-end:begin (patch-class-blossom.py)
	/** 종료 카드 「이번 판 +N송이」. 재료는 |cherishresult| 의 blossom — 이긴 판에만 온다. */
	renderCherishBlossomEnd(r: any) {
		const b = r?.blossom;
		if (!b || !(b.gained > 0)) return null;
		return <div class="cw-end-blossom">
			<b>{b.emoji} +{b.gained}송이</b>
			<small>이번 주 내 송이 {b.weekPoints.toLocaleString()}</small>
		</div>;
	}
	// cherish-blossom-end:end
	// cherish-learn-end:begin (patch-battle-learn-ui.py)
	/**
	 * 배틀 종료 카드 「새 기술을 배웠어요」 칸 (2026-10-27). 이 판으로 배운 기술이 없으면 아무것도 안 그린다.
	 * 문장 조각은 서버가 준다(subject = 이름 + 이/가, 클라가 끝 한 글자를 떼어 이름만 굵게). 기술 타입·분류 뱃지는 Dex 가 그린다.
	 */
	renderCherishLearnEnd(r: any) {
		const list: any[] = Array.isArray(r?.learned) ? r.learned : [];
		// 핫타임 판 띠 (2026-10-27) — 외형 cherish-hot.css (patch-hot-time-ui.py). 배운 기술이 없어도 띠는 그린다
		// 아이별 「오른 점수 / 지금 가진 점수」만 보인다. 계산 방식(분당)·수치표는 유저에게 안 보인다 (10-28 사용자 지시)
		const hot = r?.hot;
		const hotMons: any[] = Array.isArray(hot?.mons) ? hot.mons : [];
		const hotAt = hot?.learnAt || 10;
		const hotPt = (v: number) => (v / 10).toFixed(1);
		const hotStrip = hot ? <div class="cw-end-hot">
			<span class="cw-hot-tag">핫타임</span>
			{/* 10-29 절반 하락 — 비율은 안 쓴다(계산 방식 비공개). 하루 5판 보호 소진 줄은 상한이 없어져 걷었다 */}
			{hot.protectedLoss ? <span>핫타임이라 레이팅이 덜 깎였어요</span> : null}
			{hotMons.length ? <div class="cw-hot-mons">
				<small class="cw-hot-mons-head">기술 포인트</small>
				{hotMons.map((m: any) => <span class="cw-hot-mon">
					<b>{m.name}{m.watch ? <i class="cw-hot-watch">응원석</i> : null}</b>
					<em>+{hotPt(m.gained)}</em>
					<span class="cw-hot-mon-bar"><span style={`width:${Math.round((Math.max(0, Math.min(hotAt, m.points)) / hotAt) * 100)}%`} /></span>
					<small>{hotPt(m.points)} / {hotAt / 10}</small>
				</span>)}
			</div> : typeof hot.gained === 'number' ? <span class="cw-hot-pts">나간 아이마다 기술 포인트 +{hot.gained / 10}</span> : null}
			{/* 관전석 아이의 추억의 힘 (10-29) — 오른 양과 오른 능력(결과)만, 계산 방식은 안 쓴다. 능력 이름은 10-30 (옛 api 면 없음) */}
			{hotMons.filter((m: any) => m.ev > 0).map((m: any) => <span class="cw-hot-pts">{m.name} 추억의 힘 +{m.ev}{m.evStatKo ? ` (${m.evStatKo})` : ''}</span>)}
		</div> : null;
		if (!list.length) return hotStrip;
		return [hotStrip, <div class="cw-end-learn" role="status">
			<div class="cw-learn-head">
				<span class="cw-learn-spark" aria-hidden><svg viewBox="0 0 24 24" width="14" height="14"><path d="M12 2.5l2.2 6.3 6.3 2.2-6.3 2.2L12 19.5l-2.2-6.3L3.5 11l6.3-2.2z" fill="currentColor" /></svg></span>
				<b>새 기술을 배웠어요</b>
				{list.length > 1 ? <small>{list.length}마리</small> : null}
			</div>
			{list.map((x: any, i: number) => {
				const mv = Dex.moves.get(x.move || '');
				const base = `/cherish/sprite/${x.shiny ? 'shiny/' : ''}${x.spriteId}.png?v=2`;
				const art = x.customFormId ? `/cherish/form/${x.customFormId}/${x.spriteId}.png?v=1` : base; // CARD_V — patch-custom-form.py 와 같은 값
				// 굵게는 이름만 (10-28 사용자 지적 「디아가」 → 「**디아**가」). 조사는 서버 pickJosa 가 늘 한 글자(이/가)다
				const subj = String(x.subject || '');
				const who = subj.length > 1 ? subj.slice(0, -1) : subj;
				const josa = subj.length > 1 ? subj.slice(-1) : '';
				return <div class="cw-learn-row" style={`--i:${i}`}>
					<span class="cw-learn-art"><img src={art} data-cwfb={base} onError={this.cherishLearnArtFb} alt="" width="44" height="44" /></span>
					<span class="cw-learn-body">
						<span class="cw-learn-who"><b>{who}</b>{josa} {x.via === 'hot' ? '핫타임 배틀에서 배워 왔어요' : '배틀하면서 배워 왔어요'}</span>
						<span class="cw-learn-move">
							<strong>{x.moveKo || mv.name}</strong>
							{mv.exists ? <span dangerouslySetInnerHTML={{ __html: Dex.getTypeIcon(mv.type) }} /> : null}
							{mv.exists ? <span dangerouslySetInnerHTML={{ __html: Dex.getCategoryIcon(mv.category) }} /> : null}
						</span>
					</span>
					<span class={`cw-learn-tag${x.equipped ? ' is-on' : ''}`}>{x.equipped ? '바로 장착했어요' : '내 포켓몬에서 장착'}</span>
				</div>;
			})}
		</div>];
	}
	/** 특별폼 그림이 없으면 보통 그림으로 한 번만 내려간다 (루프 클로저 없이 data 속성으로). */
	cherishLearnArtFb = (e: any) => {
		const img = e.currentTarget as HTMLImageElement;
		const fb = img.dataset.cwfb || '';
		if (!fb || img.dataset.cwLearnFb) return;
		img.dataset.cwLearnFb = '1';
		img.src = fb;
	};
	// cherish-learn-end:end
	// cherish-coach-notes-end:begin (patch-coach-notes-tab.py)
	/** 종료 카드 — 담임이 이 판에 말했으면 「담임 노트 보기」 한 줄. 교실·시험 판과 안 말한 판은 null. */
	renderCherishCoachNotesEnd() {
		const room = this.props.room as any;
		const n: number = room.cherishCoachCount || 0;
		if (!n || room.cherishSchoolCardSeen) return null;
		return <div class="cw-end-note is-coach">
			담임이 이 판에서 {n}줄 짚어 줬어요.
			<button class="button cw-linkish cw-end-coach-go" data-href="/" onClick={() => { (window as any).cherishWantMonTab = 'coach'; }}>📓 담임 노트 보기</button>
		</div>;
	}
	// cherish-coach-notes-end:end
	// cherish-bshell:begin (patch-battle-shell.py)
	/** 새 껍데기 게이트 — index-new.html 인라인 스크립트가 ?cwpreview / localStorage cw-bshell 로 html 에 단다. */
	static cherishShellOn() {
		return document.documentElement.classList.contains('cw-bshell');
	}
	/**
	 * 조작부 머리줄 — 채팅 입력줄(ChatTextEntry)·유저 목록(ChatUserList)을 안 그리므로 거기 있던 버튼을 여기로.
	 * 기권 · 무승부 제안 (참가자, 진행 중) · N명(관전 목록) · 음량 · 타이머(내 요청이 있을 때).
	 * ⚙ 배틀 옵션은 2026-09-09 제거 — 팝업 18줄이 전부 무효(레이아웃·타이머 자동)·중복(기권·무승부·채팅 무시 2쌍)
	 * ·해로움(별명 무시 = 우리 아이 이름이 종 이름으로)이었다. 상세는 docs/showdown-current-state.md.
	 */
	/**
	 * 이 방이 구름 미궁 몇 단계인가 (미궁 방이 아니면 null).
	 *
	 * ⚠️ **홈 카드가 얹어 둔 값에 기대면 안 된다.** 그 값은 홈이 그려질 때 채워지는데,
	 *    배틀 방에 들어와 있으면 홈은 안 그려진다 — 정작 필요한 순간에 늘 비어 있다
	 *    (2026-09-09 실제로 안 뜬다는 신고). 그래서 **방마다 한 번** api 에 직접 물어본다.
	 * 진행 중 런의 stage 는 **클리어한 수**라, 지금 싸우는 판은 stage + 1 이다.
	 */
	cherishMazeStageOf(room: any) {
		if (!String(room.id || '').startsWith('battle-gen9cherishmaze-')) return null;
		if (room.cherishMazeStage === undefined) {
			// 홈이 이미 알고 있으면 그걸로 먼저 그린다 (깜빡임 방지). 없으면 null 로 두고 답을 기다린다.
			room.cherishMazeStage = (PS as any).cherishMazeStage ?? null;
			fetch('https://cherish-world.com/api/showdown/maze', { credentials: 'include' })
				.then(r => r.json())
				.then(json => {
					const run = json && json.ok ? json.data?.status?.run : null;
					room.cherishMazeStage = run && run.status === 'active' ? run.stage + 1 : null;
					this.forceUpdate();
				})
				.catch(() => {});
		}
		return room.cherishMazeStage;
	}
	renderCherishShellBar() {
		const room = this.props.room;
		const playing = !!room.side && !room.battle.ended;
		const myTurn = !!(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid);
		const mazeStage = this.cherishMazeStageOf(room);
		return <div class="cw-bbar">
			{mazeStage ? <span class="cw-bbar-stage">구름 미궁 {mazeStage}단계</span> : null}
			{playing && <button data-href="forfeitbattle" class="button cw-bbar-quit">기권</button>}
			{/* 무승부 제안 버튼은 2026-09-26 제거 — 서버 명령 화이트리스트(patch-cmd-whitelist.py)에 /offertie 가 없어
			    누르면 영어 「does not exist」만 떴다(로켓단 판에서 사용자 발견). NPC 는 받을 수도 없다. */}
			{/* 약점 가이드 · 대회 관전 띠 — 컴포넌트·import·로드 태그는 patch-weakness-guide.py · patch-tournament-panel.py 소유,
			    호출은 블록 주인인 여기가 든다 (2026-10-17, 09-19 규약). 예전엔 저쪽이 이 자리에 한 줄씩 끼워 넣어서 이 패치를
			    다시 바를 때마다 버튼이 사라졌다(09-15 약점 가이드 실종). 저쪽 스크립트가 아직 안 실렸어도 안 죽게 typeof 로 가드한다. */}
			{typeof CherishWeaknessGuide === 'function' && room.side && <CherishWeaknessGuide room={room} />}
			{typeof CherishTourStrip === 'function' && <CherishTourStrip room={room} />}
			<span class="cw-bbar-sp"></span>
			<button data-href="userlist" class="button" title="참여 중인 트레이너"><i class="fa fa-users" aria-hidden></i> {room.userCount}</button>
			<button data-href="volume" class="button" title="음량"><i class="fa fa-volume-up" aria-hidden></i></button>
			{myTurn && <TimerButton room={room} top={0} />}
		</div>;
	}
	/**
	 * 로그에만 실리던 줄의 새 자리 (2단계). 카운트다운(「N초 남았어요」)은 필드 HUD 가 보여 주므로 안 띄운다.
	 *  - |inactive|/|inactiveoff| 켜짐·꺼짐·연결 끊김·재접속 → 토스트   - |error|/|bigerror| → 빨간 토스트
	 *  - |html| broadcast-blue(cherish-battle-end 의 레이팅 안내) → 종료 카드 안내 줄   - |uhtml|cherishdash|(어린이집 배너, 학교 제외) → 흰 토스트
	 *  - |win|/|tie|/|-message| → 종료 카드 재료
	 */
	cherishShellLine(args: Args) {
		const room = this.props.room as any;
		const live = !!room.battle?.atQueueEnd; // 재입장 때 쏟아지는 옛 줄엔 토스트를 안 띄운다
		switch (args[0]) {
		case 'win': room.cherishEnd = { winner: args[1] || '', tie: false }; break;
		case 'tie': room.cherishEnd = { winner: '', tie: true }; break;
		case '-message': {
			// 배틀을 **끝내는** 줄만 종료 카드 사유로 받는다 (2026-10-21). 예전엔 받은 줄을 전부 덮어써서
			// 판 중간의 「Sleep Clause Mod activated.」 가 정상 승패 아래 사유처럼 남았다.
			// 문구는 CherishKoMessage(patch-ko-battle-messages.py) 가 한글로 바꾼다. 판별은 영어 원문으로.
			const raw = args[1] || '';
			const koMsg = (window as any).CherishKoMessage;
			let t = koMsg ? koMsg(raw) : raw;
			if (/ forfeited\.$/.test(t)) t = t.replace(/ forfeited\.$/, ' 님이 기권했어요.');
			if (t === 'All players are inactive.') t = '모두 자리를 비워서 무승부예요.';
			if (/Endless Battle Clause activated!$/.test(raw)) {
				room.cherishEndReason = '더 이상 배틀을 진행할 수 없어서 무승부로 끝났어요.';
			} else if (/ forfeited(?: by changing their name)?\.$| lost by having an inappropriate name\.$|^All players are inactive\.$| Pokemon left(?:;|$)| total HP left(?:;|$)/.test(raw)) {
				if (t) room.cherishEndReason = t;
			}
			break;
		}
		case 'inactive': case 'inactiveoff': {
			const t = args[1] || '';
			if (!live || t.startsWith('남은 시간: ') || /남았어요\.$/.test(t)) break;
			this.cherishToast(BattleLog.escapeHTML(t), 'timer', 4500);
			break;
		}
		case 'error': case 'bigerror':
			// 문구는 서버가 한글로 바꿔 보내지만(patch-ko-choice-error-server.py) **머리표는 영어로 남겨 둔다** —
			// 위 `case 'error'` 분기가 `[Invalid choice]` 를 보고 선택 상태를 되돌리기 때문이다. 그래서 화면에
			// 띄울 때만 떼낸다. ⚠️ **머리표는 둘이다** — sim 은 요청을 갱신한 경우 `[Unavailable choice]` 를 보낸다
			// (`sim/side.ts` emitChoiceError). 그쪽이 오히려 흔하다: 기술이 막혔을 때·붙잡혔을 때가 전부 여기다.
			// 하나만 떼면 그 둘이 영어로 샌다 (2026-09-21 실측, 운영 js 에 Unavailable 처리 0건).
			// |bigerror| 는 sim 이 영어로 보낸다(1000턴 자동 무승부 예고) — CherishKoMessage 로 한글화 (2026-10-21).
			if (live) {
				const koMsg = (window as any).CherishKoMessage;
				const e = args[0] === 'bigerror' && koMsg ? koMsg(args[1] || '') : (args[1] || '');
				this.cherishToast(BattleLog.escapeHTML(e.replace(/^\[(?:Invalid|Unavailable) choice\]\s*/, '')), 'error', 5000);
			}
			break;
		case 'html': case 'raw': {
			const h = args[1] || '';
			if (h.includes('broadcast-blue')) {
				(room.cherishEndNotes ||= []).push($('<div></div>').html(BattleLog.sanitizeHTML(h)).text().trim());
				room.update(null);
			}
			break;
		}
		case 'uhtml':
			if (live && args[1] === 'cherishdash' && !(args[2] || '').includes('체리의 포켓몬학교')) {
				this.cherishToast(BattleLog.sanitizeHTML(args[2] || ''), 'dash', 9000);
			}
			break;
		}
	}
	/** 필드 위 가운데 토스트 (최대 2개, ms 뒤 사라짐). 담임 말풍선·곡명 토스트와 같은 컨테이너(scene.$battle). */
	cherishToast(html: string, kind: string, ms: number) {
		const $battle: JQuery | undefined = (this.props.room.battle?.scene as any)?.$battle;
		if (!$battle || !html) return;
		let $wrap = $battle.children('.cw-toasts');
		if (!$wrap.length) {
			$wrap = $('<div class="cw-toasts"></div>');
			$battle.append($wrap);
		}
		const $t = $('<div class="cw-toast cw-toast-' + kind + '"></div>').html(html);
		$wrap.append($t);
		while ($wrap.children().length > 2) $wrap.children().first().remove();
		setTimeout(() => {
			$t.addClass('cw-toast-out');
			setTimeout(() => $t.remove(), 500);
		}, ms);
	}
	/**
	 * 구름 미궁 — 종료 카드에서 **배틀 화면을 안 떠나고** 이어간다 (2026-09-08 사용자:
	 * 「배틀 화면에서 이기면 그대로 다음 층 이어가게는 안 되나?」).
	 * 재료는 |cherishresult| 에 실려 온다(patch-battle-ended.py → api). 카드 상태 정본은 서버라
	 * 방을 나가서 홈 카드로 이어가도 어긋나지 않는다.
	 */
	/**
	 * 끝난 미궁 방 탭을 닫는다 (2026-09-08 사용자: 「탭이 하나씩 늘어나네」).
	 * 다음 단계 방은 api 가 이미 열어 뒀으므로 여기서 지금 방을 나가도 화면이 비지 않는다.
	 */
	cherishMazeCloseDone() {
		try {
			for (const id in PS.rooms) {
				if (!id.startsWith('battle-gen9cherishmaze-')) continue;
				if ((PS.rooms[id as RoomID] as any)?.battle?.ended) PS.leave(id as RoomID);
			}
		} catch {}
	}
	/*
	 * 종료 카드가 보는 값을 서버 상태로 갱신한다 (2026-09-09).
	 *
	 * push 는 배틀이 끝날 때 한 번뿐이라, 파티에서 **상대가 보상을 고르면 내 화면은 모른다**.
	 * 방장이 「앞에 섰던 사람이 고르는 중」에 갇혀 다음 단계를 못 열던 사고가 이것이다.
	 * ⚠️ 파티일 때만 켠다 — 혼자면 내 조작 말고 상태가 바뀔 일이 없다.
	 *
	 * ⚠️⚠️ **타이머를 걷었다 (2026-09-23).** 전엔 3초짜리 되풀이 타이머를 **방마다** 걸고
	 * 멈추는 길이 「방이 사라졌나」뿐이었다. 쇼다운은 끝난 배틀 방을 닫지 않고 쌓아두고, 타이머
	 * 안에서 `mazeRunEnded` 를 다시 보지도 않으며, 끄는 길인 render 는 방을 떠나면 멈춘다 —
	 * 그래서 미궁을 연달아 돌면 **방 N개 = 3초마다 N건**이 되었다. 2026-09-22 실측: 한 사람이
	 * `GET /showdown/maze` 6,325건(형제 home 226·school 144 대비 28~88배)을 쏘고 그중 5,128건을
	 * 제 바구니(60/분)에서 429 로 맞았다. 그 사람 화면은 rooms·home 까지 막혀 멈췄다.
	 * 이제 동행자가 고르면 서버가 `|cherishmaze|` 한 장을 보내고(patch-party.py `mazeping`),
	 * 그 신호만 이 tick 을 부른다. 요청은 「3초마다」에서 「상대가 고를 때 한 번」이 됐다.
	 */
	cherishMazeSync(room: any, on: boolean) {
		if (on && !room.cherishMazeHook) {
			const tick = () => {
				// ⚠️ 방을 떠나면 렌더가 멈춰 끄는 길이 없어진다 — 여기서 스스로 거둔다
				if (!PS.rooms[room.id]) { this.cherishMazeSync(room, false); return; }
				fetch('https://cherish-world.com/api/showdown/maze', { credentials: 'include' })
					.then(res => res.json())
					.then(json => {
						const run = json && json.ok ? json.data?.status?.run : null;
						const r = room.cherishResult;
						if (!r || !run) return;
						// 서버가 정본 — 카드·팀·차례를 통째로 갈아 끼운다
						const justPicked = r.mazeCards?.length && !run.cards?.length && Number(run.stage) === Number(r.stage);
						r.mazeCards = run.cards && run.cards.length ? run.cards : null;
						if (run.lastCard) room.cherishMazeGot = run.lastCard;
						if (justPicked && (window as any).CherishMazeFX) (window as any).CherishMazeFX.reveal(this.base, run);
						r.mazeTeam = run.team || r.mazeTeam;
						r.mazeNext = run.next || r.mazeNext;
						if (run.party) {
							r.mazeLeader = run.party.leader === true;
							r.mazeCardMine = run.party.cardMine === true;
						}
						this.forceUpdate();
					})
					.catch(() => {});
			};
			// 타이머가 아니다 — 동행자가 뭔가 고르면 서버가 `|cherishmaze|` 한 장을 보내고
			// (patch-party.py `mazeping`), 그 신호가 여기 등록한 tick 을 부른다. 켤 때 한 번은
			// 직접 받아 둔다 (그 사이에 이미 바뀐 것을 놓치지 않으려고).
			room.cherishMazeHook = tick;
			const w = window as any;
			if (!w.cherishMazeHooks) w.cherishMazeHooks = new Set();
			w.cherishMazeHooks.add(tick);
			tick();
		} else if (!on && room.cherishMazeHook) {
			(window as any).cherishMazeHooks?.delete(room.cherishMazeHook);
			room.cherishMazeHook = null;
		}
	}
	renderCherishMazeEnd(r: any) {
		if (!r || !r.maze) return null;
		const room = this.props.room as any;
		if (r.mazeRunEnded) {
			return <div class="cw-end-maze">
				<div class="cw-end-maze-depth"><b>{r.stage}단계</b>에서 끝났어</div>
				<div class="cw-end-note">다음 미궁에서 더 깊이 가 보자.</div>
			</div>;
		}
		const post = (path: string, body: any, after?: () => void) => {
			if (room.cherishMazeBusy) return;
			room.cherishMazeBusy = true;
			room.cherishMazeErr = '';
			this.forceUpdate();
			fetch(`https://cherish-world.com/api/showdown/maze${path}`, {
				method: 'POST', credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body || {}),
			})
				.then(res => res.json())
				.then(json => {
					room.cherishMazeBusy = false;
					if (!json || !json.ok) room.cherishMazeErr = (json && json.message) || '지금은 이어갈 수 없어요.';
					else {
						// 고른 카드가 한 일 한 줄 — 몸풀기는 무작위라 **그 자리에서** 안 보여 주면 알 길이 없다
						if (json.data && json.data.lastCard) room.cherishMazeGot = json.data.lastCard;
						// 선봉을 바꾸면 순서가 달라진다 — 응답의 팀으로 갈아 끼워야 화면이 따라간다
						if (json.data && json.data.team) r.mazeTeam = json.data.team;
						if (path === '/card' && (window as any).CherishMazeFX) {
							(window as any).CherishMazeFX.reveal(this.base, json.data);
						}
						if (after) after();
						// 동행자 화면도 바뀌어야 한다 — 신호 한 장(patch-party.py `mazeping`). 파티가
						// 아니면 서버가 상대를 못 찾아 조용히 버린다. 이게 옛 3초 폴링을 대신한다.
						try { PS.send('/cherishparty mazeping'); } catch {}
					}
					this.forceUpdate();
				})
				.catch(() => { room.cherishMazeBusy = false; room.cherishMazeErr = '연결이 불안정해요.'; this.forceUpdate(); });
		};
		const cards = r.mazeCards as any[] | null;
		// 파티면 상대 조작으로 상태가 바뀐다 — 런이 끝나지 않았으면 서버를 따라간다
		// 내가 **단을 기다리는** 때만 돌린다 — 혼자면 둘 다 true 라 안 돌고, 그게 맞다
		this.cherishMazeSync(room, !r.mazeRunEnded && (r.mazeCardMine === false || r.mazeLeader === false));
		// 이번에 친 단계. 종료 훅이 결과에 실어 준다 (results[userid].stage).
		const stageNow = Number(r.stage || 0);
		const got = room.cherishMazeGot as string | null;
		const pick = room.cherishMazePick;
		const busy = !!room.cherishMazeBusy;
		// 2인 파티 — 다음 단계는 **방장만**, 보상 카드는 **그 라운드 선봉**이 고른다 (api 도 거절한다).
		const canAct = r.mazeLeader !== false;
		const cardMine = r.mazeCardMine !== false;
		// 선봉은 **그 차례 주인**이 정한다 (2026-09-09) — 방장이 동행자 차례를 덮으면 교대가 무너진다
		const leadMine = r.mazeLeadMine !== false;
		let body;
		if (cards && cards.length && !cardMine) {
			body = <div class="cw-end-note">앞에 섰던 사람이 이번 보상을 고르는 중이야.</div>;
		} else if (!cards?.length && !canAct) {
			body = <div class="cw-end-note">방장이 다음 단계를 열어 줄 거야.</div>;
		} else if (cards && cards.length) {
			if (pick !== null && pick !== undefined && cards[pick] && cards[pick].options && cards[pick].options.length) {
				const card = cards[pick];
				const targets = card.targets as any[] | undefined;
				// ⚠️ 「← 다시 고르기」는 뺐다 — 선택지를 본 뒤 무를 수 있으면 카드 고르기가 공짜 정찰이 된다.
				const itemPick = room.cherishMazeItemPick ?? null;
				if (targets && targets.length && itemPick === null) {
					body = <div class="cw-end-maze-cards">
						<p class="cw-end-maze-q"><b>{card.title}</b> — 어느 도구를 주울까?</p>
						{card.options.map((o: any) => <button class="button cw-end-maze-opt" disabled={busy}
							onClick={() => { room.cherishMazeItemPick = o.key; this.forceUpdate(); }}>
							<b>{o.label}</b>
							{o.desc ? <small>{o.desc}</small> : null}
						</button>)}
					</div>;
				} else if (targets && targets.length) {
					const chosenLabel = (card.options.find((o: any) => o.key === itemPick) || {}).label || '도구';
					body = <div class="cw-end-maze-cards">
						<p class="cw-end-maze-q"><b>{chosenLabel}</b> — 누구에게 줄까?</p>
						{targets.map((t: any) => <button class="button cw-end-maze-opt" disabled={busy}
							onClick={() => post('/card', { index: pick, option: `${itemPick}|${t.key}` }, () => {
								r.mazeCards = null; room.cherishMazePick = null; room.cherishMazeItemPick = null;
							})}>
							{t.sprite ? <img class="cw-maze-optsp" src={`/cherish/sprite/${t.sprite}.png?v=2`} width="32" height="32" alt="" /> : null}
							<b>{t.label}</b>
						</button>)}
					</div>;
				} else {
				body = <div class="cw-end-maze-cards">
					<p class="cw-end-maze-q"><b>{card.title}</b>, 어느 아이에게?</p>
					<p class="cw-end-maze-q">{card.desc}</p>
					{card.options.map((o: any) => <button class="button cw-end-maze-opt" disabled={busy}
						onClick={() => post('/card', { index: pick, option: o.key }, () => { r.mazeCards = null; room.cherishMazePick = null; })}>
						{o.sprite ? <img class="cw-maze-optsp" src={`/cherish/sprite/${o.sprite}.png?v=2`} width="32" height="32" alt="" /> : null}
						<b>{o.label}</b>
						{o.desc ? <small>{o.desc}</small> : null}
					</button>)}
				</div>;
				}
			} else {
				body = <div class="cw-end-maze-cards">
					<p class="cw-end-maze-q">하나만 가져갈 수 있어. 랜덤 효과는 선택한 뒤 정해져.</p>
					<div class="cw-end-maze-row">
						{cards.map((c: any, i: number) => <button class="cw-end-maze-card" data-maze-kind={c.kind} data-maze-stat={c.stat || undefined} disabled={busy}
							onClick={() => {
								if (c.options && c.options.length) { room.cherishMazePick = i; this.forceUpdate(); }
								else post('/card', { index: i, option: null }, () => { r.mazeCards = null; });
							}}>
							<b>{c.title}</b><small>{c.desc}</small>
						</button>)}
					</div>
				</div>;
			}
		} else {
			const next = r.mazeNext;
			body = <button class="button cw-end-maze-next" disabled={busy}
				onClick={() => post('/next', {}, () => this.cherishMazeCloseDone())}>
				{busy ? '여는 중…' : next ? `${next.stage}단계로 나아가기${next.foeCount > 1 ? ` (야생 ${next.foeCount}마리)` : ''}` : '나아가기'}
			</button>;
		}
		const team = r.mazeTeam as any[] | null;
		return <div class="cw-end-maze">
			<div class="cw-end-maze-depth"><b>{r.stage}단계</b> 돌파</div>
			{/* 방금 고른 카드가 한 일. 몸풀기는 능력이 무작위라 여기서 안 보여 주면
			    다음 라운드 홈 카드까지 가야 뭐가 올랐는지 알 수 있었다 (2026-09-09 사용자 지적). */}
			{got ? <div class="cw-end-maze-got">{got}</div> : null}
			{team && team.length ? <ul class="cw-end-maze-team">
				{team.map((p: any, i: number) => <li class={p.fainted ? 'is-fnt' : ''}>
					{/*
					  선봉 고르기 — **여기서 정해야 한다** (2026-09-09 사용자).
					  체력이 5% 남은 아이가 그대로 앞에 서면 다음 판을 그냥 버리게 된다.
					  파티에서는 방장만 (api 도 거절한다). 쓰러진 아이는 앞에 못 세운다.
					*/}
					{i === 0 ? <span class="cw-end-maze-first is-on">선봉</span>
						: p.fainted || !leadMine || p.mine === false ? <span class="cw-end-maze-first" />
						: <button class="cw-end-maze-first" disabled={busy} title="선봉으로 세우기"
							onClick={() => post('/lead', { index: i })}>선봉으로</button>}
					{p.sprite ? <img class="cw-maze-sp" src={`/cherish/sprite/${p.sprite}.png?v=2`} width="26" height="26" alt="" /> : null}
					<span class="cw-end-maze-mon" data-maze-mon={p.name}>{p.name}
						{p.boosts?.length ? <small style="display:block;white-space:normal">{p.boosts.map((b: any) => `${b.label} ${b.n > 0 ? '+' : ''}${b.n}`).join(', ')}</small> : null}
					</span>
					<span class="cw-end-maze-bar"><i style={`width:${p.fainted ? 0 : Math.max(3, p.hp)}%`}></i></span>
					<span class="cw-end-maze-hp">{p.fainted ? '쓰러짐' : `${p.hp}%`}</span>
				</li>)}
			</ul> : null}
			{body}
			{room.cherishMazeErr ? <div class="cw-end-note">{room.cherishMazeErr}</div> : null}
		</div>;
	}
	/** 종료 카드 (3단계): 결과 배지 · 사유 · 턴 수 · 레이팅 안내 · 내 팀 · 버튼. 버튼 줄은 기존 after-row 마크업을 그대로 쓴다(이름 유지). */
	renderCherishEndCard() {
		const room = this.props.room as any;
		const battle = room.battle;
		const end = room.cherishEnd || { winner: '', tie: false };
		const my = battle.mySide;
		/*
		 * ⚠️ 미궁은 **서버 판정을 쓴다** (2026-09-09). 이름 대조는 파티에서 깨졌다 — 동승자를
		 * 서버가 붙이므로(cherishAttachAlly) 클라의 mySide.ally 가 비어 있을 수 있고,
		 * 그러면 |win| 이름이 내 자리 이름과 안 맞아 **이겼는데 패배**로 뜼다.
		 */
		/*
		 * 2대2 도 **서버 판정을 먼저 본다** (2026-09-19). 업스트림 sim 의 win() 이 2대2 에서
		 * `|win|A & B` 로 **두 이름을 한 덩어리**로 보낸다 (sim/battle.ts: `side.name + ' & ' + allySide.name`).
		 * 그래서 이름 대조가 양 편 **모두** 빗나갔고, 이긴 팀에도 「패배」가 떴다 (2026-09-19 실측:
		 * battle-gen9cherishmultibattle-9621 에서 Elo 가 +15 오른 승자 화면이 「패배」).
		 * api 가 참가자 전원에게 `won` 을 실어 보내므로 그걸 쓰고, 옛 api 대비 폴백만 이름으로 간다.
		 */
		const mazeR = room.cherishResult as { maze?: boolean, mazeWon?: boolean, won?: boolean } | undefined;
		const winNames = end.winner ? end.winner.split(' & ') : [];
		const myNames = [my?.name, my?.ally?.name].filter((n): n is string => !!n);
		const won = mazeR && mazeR.maze
			? mazeR.mazeWon === true
			: typeof mazeR?.won === 'boolean'
				? mazeR.won
				: (!end.tie && winNames.length > 0 && myNames.some(n => winNames.includes(n)));
		let badge: string;
		let cls: string;
		if (!room.side) {
			badge = end.tie ? '무승부' : end.winner ? `${end.winner} 승리` : '배틀 종료';
			cls = 'spec';
		} else if (end.tie) {
			badge = '무승부';
			cls = 'tie';
		} else if (won) {
			badge = '승리!';
			cls = 'win';
		} else {
			badge = '패배';
			cls = 'lose';
		}
		const notes: string[] = room.cherishEndNotes || [];
		// 이 판의 수치 (|cherishresult|, 이 유저에게만): Elo 변동 · 오늘의 배틀 점수. 없으면 칸 자체를 안 그린다.
		const r = room.cherishResult as { elo?: { after: number, delta: number } | null, points?: { gained: number, points: number, stage: number, stageBefore: number, pending?: number, eggAdded?: number, cherryAdded?: number, pendingCherry?: number, dustAdded?: number, pendingDust?: number } | null, towerCapped?: boolean } | undefined;
		const elo = r?.elo || null;
		const pts = r?.points || null;
		const sign = (n: number) => (n > 0 ? '+' : '') + n;
		// 알·체리는 수령제다 (2026-09-07) — 이 판으로 「받을 몫」이 늘었을 뿐 아직 손에 없다. 홈으로 보내는 게 이 칸의 일이다.
		// ⚠️ 단계 차이로 세면 틀린다 (보상 개편 0298: 단계마다 나오는 게 달라 「두 단계 넘겼지만 알 0개」가 정상).
		// eggAdded 가 없는 옛 응답만 단계 차이로 떨어진다.
		const eggGot = pts ? (typeof pts.eggAdded === 'number' ? pts.eggAdded : Math.max(0, pts.stage - pts.stageBefore)) : 0;
		const cherryGot = pts && typeof pts.cherryAdded === 'number' ? pts.cherryAdded : 0;
		// 2026-09-09 개편으로 중간 단계 보상이 베리다. 이걸 안 보면 단계를 넘겨도 칸 자체가 안 뜬다.
		const dustGot = pts && typeof pts.dustAdded === 'number' ? pts.dustAdded : 0;
		const eggPending = pts && typeof pts.pending === 'number' ? pts.pending : eggGot;
		const cherryPending = pts && typeof pts.pendingCherry === 'number' ? pts.pendingCherry : cherryGot;
		const dustPending = pts && typeof pts.pendingDust === 'number' ? pts.pendingDust : dustGot;
		const wonAny = eggGot > 0 || cherryGot > 0 || dustGot > 0;
		return <div class={`inline-controls cw-end is-${cls}`}>
			{/* 히어로 — 결과 톤 배경(그림 한 장, 없으면 CSS 그라데이션 폴백) 위에 배지·사유·턴. 외형 patch-skin.py §33. */}
			<div class="cw-end-hero">
				<div class={`cw-end-badge cw-end-${cls}`}>{badge}</div>
				{room.cherishEndReason && <div class="cw-end-sub">{room.cherishEndReason}</div>}
				<div class="cw-end-meta">{battle.turn}턴</div>
			</div>
			{(elo || pts) && <div class="cw-end-stats">
				{elo && <div class="cw-end-stat">
					<b class={elo.delta > 0 ? 'up' : elo.delta < 0 ? 'dn' : ''}>{sign(Math.round(elo.delta))}</b>
					<small>레이팅 {Math.round(elo.after).toLocaleString()}</small>
				</div>}
				{pts && <div class="cw-end-stat">
					<b>{sign(pts.gained)}</b>
					<small>오늘의 배틀 점수 {pts.points.toLocaleString()}</small>
				</div>}
				{wonAny && <div class="cw-end-stat cw-end-egg">
					<b>{eggGot > 0 ? `알 +${eggGot}` : cherryGot > 0 ? `체리 +${cherryGot.toLocaleString()}` : `베리 +${dustGot.toLocaleString()}`}</b>
					<small>{eggGot > 0 && eggPending > eggGot ? `받을 알 ${eggPending}개`
						: eggGot === 0 && cherryGot > 0 && cherryPending > cherryGot ? `받을 체리 ${cherryPending.toLocaleString()}`
						: eggGot === 0 && cherryGot === 0 && dustPending > dustGot ? `받을 베리 ${dustPending.toLocaleString()}`
						: '배틀 점수 단계 달성'}</small>
				</div>}
			</div>}
			{wonAny && <div class="cw-end-note is-egg">메인 메뉴 「오늘의 배틀 점수」에서 {eggGot > 0 ? '알을' : cherryGot > 0 ? '체리를' : '베리를'} 받아 가요.</div>}
			{r?.towerCapped && <div class="cw-end-note">이 상대에게선 오늘 받을 점수를 다 받았어. 다른 층에서 이어 가자.</div>}
			{/*
			  「이번 판 +N송이」 — 메서드는 patch-class-blossom.py 것이고 **호출만 여기 있다** (2026-09-19).
			  원래는 그쪽 패치가 이 줄을 끼워 넣었는데, 이 블록은 patch-battle-shell.py 가 통째로
			  다시 쓰는 자리라 셸 패치를 재적용할 때마다 호출이 조용히 사라졌다 (메서드는 남아서
			  소스가 멀쩡해 보이고, 마커 검사에도 안 걸린다 — 09-18 배포 뒤 꽃송이 줄이 통째로 안 떴다).
			  호출을 블록 주인에게 옮기면 다시 지워질 자리가 없다. 패치가 안 깔렸을 때를 위해 `?.`.
			*/}
			{(this as any).renderCherishBlossomEnd?.(r)}
			{/* 새로 배운 기술 칸 (2026-10-27) — 메서드는 patch-battle-learn-ui.py 것, 호출만 여기 (꽃송이 줄과 같은 이유) */}
			{(this as any).renderCherishLearnEnd?.(r)}
			{/* 담임 노트 안내 (2026-10-30) — 메서드는 patch-coach-notes-tab.py 것, 호출만 여기 (같은 이유). 담임이 이 판에 말한 적 없으면 null */}
			{(this as any).renderCherishCoachNotesEnd?.()}
			{(room.cherishResult as any)?.gym && <div class="cw-end-note">{(room.cherishResult as any).berry > 0 ? `베리 ${(room.cherishResult as any).berry.toLocaleString()} 지급 완료` : '이번 도전 보상 없음'}</div>}
			{this.renderCherishMazeEnd(r)}
			{notes.map(n => <div class="cw-end-note">{n}</div>)}
			{this.renderTeamList()}
			{/*
			  대회 판이면 주 버튼이 「대진표로 돌아가기」다 (2026-09-19 사용자 결정) — 돌아갈 곳이
			  배틀 홈이 아니라 대진표이기 때문이다. 메서드는 patch-tournament-panel.py 것이고
			  **호출만 여기 있다**: 이 블록은 셸 패치가 통째로 다시 쓰는 자리라, 호출을 저쪽에서
			  끼워 넣으면 재적용 때마다 사라진다 (꽃송이 줄이 그랬다). 대회 판이 아니면 null 이라
			  원래 버튼 줄이 그대로 나온다. 패치가 안 깔렸을 때를 위해 `?.`.
			*/}
			<p class="cw-end-btns cw-end-main">
				{(room.cherishResult as any)?.gym ? <button class="button" onClick={() => { (window as any).cwgReturnToGym(room.id, (room.cherishResult as any).gymLive ? (room.cherishResult as any).gymType : undefined); }}>{(room.cherishResult as any).gymLive ? '실시간 로비로 돌아가기' : '체육관으로 돌아가기'}</button> : (this as any).renderCherishTourEnd?.() || (this as any).renderCherishRaidLiveEnd?.() || this.renderCherishRoomButtons()}
			</p>
		</div>;
	}
	// cherish-bshell:end
	// cherish-lesson-panel:begin (patch-school-replay.py)
	/** 3교시 리플레이 수업 — 조작부 자리에 그리는 카드 (질문 · 오답 설명 · 다음 장면 · 완료). */
	renderCherishLessonControls() {
		const room = this.props.room as any;
		const L = room.cherishLesson;
		const sc = room.cherishLessonScene();
		const total = L.data?.scenes?.length ?? 0;
		const head = <div class="cw-lesson-head">
			<span class="cw-lesson-tag">{room.cherishLessonNo()}교시 {L.data?.title || '리플레이 수업'}</span>
			{sc && <span class="cw-lesson-step">{L.sceneIndex + 1}/{total} {sc.title}</span>}
		</div>;
		const close = <button class="button cw-lesson-go" onClick={() => room.cherishLessonClose()}>메인 메뉴</button>;
		if (L.waitingCoach) {
			return <div class="cw-lesson">
				{head}
				<p class="cw-lesson-note">체리 말을 읽었으면 계속을 눌러 줘. 말풍선을 눌러도 돼.</p>
				<button class="button cw-lesson-go" onClick={() => room.cherishLessonContinue()}>계속 ▶</button>
			</div>;
		}
		switch (L.phase) {
		case 'loading':
			return <div class="cw-lesson">{head}<p class="cw-lesson-note">수업 대본을 불러오는 중…</p></div>;
		case 'intro':
			return <div class="cw-lesson">
				{head}
				<p class="cw-lesson-note">체리의 설명을 읽고 「알겠어, 시작할게」를 눌러 줘.</p>
				<button class="button cw-lesson-go" onClick={() => room.cherishLessonStartScene(0)}>바로 시작</button>
			</div>;
		case 'card':
			return <div class="cw-lesson">
				{head}
				<p class="cw-lesson-note">체리의 설명을 읽고 「알겠어, 시작할게」를 눌러 줘.</p>
				<button class="button cw-lesson-go" onClick={() => room.cherishLessonStartScene(L.sceneIndex + 1)}>다음으로</button>
			</div>;
		case 'error':
			return <div class="cw-lesson">{head}<p class="cw-lesson-note">{L.error}</p>{close}</div>;
		case 'question':
			return <div class="cw-lesson">
				{head}
				<p class="cw-lesson-q">{sc.question.text}</p>
				<div class="cw-lesson-choices">
					{sc.question.choices.map((ch: any, i: number) => (
						<button class="button cw-lesson-choice" onClick={() => room.cherishLessonChoose(i)}>
							<strong>{ch.label}</strong><small>{ch.sub}</small>
						</button>
					))}
				</div>
			</div>;
		case 'wrong-explain':
			return <div class="cw-lesson">
				{head}
				<p class="cw-lesson-q">{sc.wrongExplain}</p>
				<button class="button cw-lesson-go" onClick={() => room.cherishLessonRetry()}>다시 골라 보기</button>
			</div>;
		case 'scene-done':
			return <div class="cw-lesson">
				{head}
				<p class="cw-lesson-q">잘했어! 다음 장면으로 갈까?</p>
				<button class="button cw-lesson-go" onClick={() => room.cherishLessonNext()}>다음 장면</button>
			</div>;
		case 'done':
			return <div class="cw-lesson">
				{head}
				<p class="cw-lesson-q">{L.data?.outro}</p>
				<p class="cw-lesson-note">{L.error || `${room.cherishLessonNo()}교시 통과! 메인 메뉴로 돌아가면 홈의 학교 카드에 표시돼.`}</p>
				{close}
			</div>;
		default:
			return <div class="cw-lesson">
				{head}
				<p class="cw-lesson-note">{L.phase === 'wrong' ? '이렇게 됐을 거야…' : '체리가 보여 주는 장면을 봐 줘.'}</p>
			</div>;
		}
	}
	// cherish-lesson-panel:end
	// cherish-extraction-coop:begin
	renderExtractionCoop() {
		const room = this.props.room as any, request = room.request;
		const owners = room.cherishExtractionOwners as Record<string,string>;
		if (!request || request.wait) return <div class="messagebar">상대의 선택을 기다리고 있어요.</div>;
		const mons = request.side?.pokemon || [];
		const nameOf = (mon: any) => String(mon?.ident || '').split(': ').slice(1).join(': ');
		const ownerOf = (mon:any) => owners[`species:${String(mon.details||'').split(',')[0].toLowerCase().replace(/[^a-z0-9]/g,'')}`] ?? owners[nameOf(mon)];
		const mine = mons.map((mon:any,index:number)=>({mon,index})).filter(({mon,index}:any)=>index<2 && ownerOf(mon)===PS.user.userid && (request.forceSwitch ? request.forceSwitch[index] : request.active?.[index] && !String(mon.condition).includes('fnt')));
		const pending = room.cherishExtractionPending;
		const send = (command:string) => PS.send(command, room.id);
		const choose = (index:number,choice:string) => {
			room.cherishExtractionTarget = null;
			send(`/choose coop:${index}:${choice}|${request.rqid}`);
			this.forceUpdate();
		};
		const selected = room.cherishExtractionTarget?.rqid === request.rqid ? room.cherishExtractionTarget : null;
		return <div class="movecontrols" style="padding:8px 4px">
			<p style="margin:0 0 12px;color:#78677d;font-size:13px">각자 내 포켓몬을 지시해 주세요 · 두 사람의 선택이 모이면 턴이 진행돼요.</p>
			{!mine.length && <div class="messagebar">내 아이는 쉬고 있어요. 파트너를 응원해 주세요.</div>}
			{mine.map(({mon,index}:any)=>{
				const done = pending?.rqid===request.rqid && pending.slots?.includes(index);
				const moveRequest = request.active?.[index];
				const targetChoice = selected?.index === index ? selected : null;
				const moveTargets = (target:string) => {
					const targets:{value:number;label:string}[] = [];
					if (['normal','any','adjacentFoe'].includes(target)) (room.battle.farSide.active||[]).forEach((foe:any,j:number)=>{if(foe&&!foe.fainted)targets.push({value:j+1,label:foe.name||`상대 ${j+1}`});});
					if (['normal','any','adjacentAlly','adjacentAllyOrSelf'].includes(target)) mons.slice(0,2).forEach((ally:any,j:number)=>{if(!String(ally.condition).includes('fnt')&&(j!==index||target==='adjacentAllyOrSelf'))targets.push({value:-(j+1),label:nameOf(ally)});});
					return targets;
				};
				const chosenMove = targetChoice && moveRequest?.moves?.[targetChoice.move];
				return <section key={index} style="margin:0 0 14px">
					<h3 style="margin:8px 0 12px">{nameOf(mon)}{done ? ' · 선택 완료' : ' · 기술 선택'}</h3>
					{done ? <div class="messagebar">선택을 보냈어요. 친구의 선택을 기다리고 있어요.</div> :
					request.forceSwitch ? <div class="switchmenu">{mons.map((target:any,i:number)=>!target.active&&!String(target.condition).includes('fnt')&&ownerOf(target)===PS.user.userid?<button key={i} class="button" onClick={()=>choose(index,`switch ${i+1}`)}>{nameOf(target)}로 교체</button>:null)}</div> :
					chosenMove ? <div>
						<p>기술을 사용할 대상을 골라 주세요.</p>
						<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px">
							{moveTargets(chosenMove.target).map(t=><button key={t.value} class="button" style="padding:14px;border-radius:12px" onClick={()=>choose(index,`move ${targetChoice.move+1} ${t.value}`)}><small>{t.value>0?'상대':'우리 편'}</small><br/><strong>{t.label}</strong></button>)}
						</div>
						<button class="button" style="margin-top:10px" onClick={()=>{room.cherishExtractionTarget=null;this.forceUpdate();}}>기술 다시 고르기</button>
					</div> : <div class="movemenu" style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px">
						{(moveRequest?.moves||[]).map((move:any,i:number)=>{
							const moveData = Dex.moves.get(move.id || move.move);
							const label = (moveData?.exists && (Dex.text.get(moveData) as any).name) || move.move || move.name || move.id;
							const disabled = move.disabled || move.pp===0;
							const targets = moveTargets(move.target);
							const needsTarget = ['normal','any','adjacentFoe','adjacentAlly','adjacentAllyOrSelf'].includes(move.target);
							return <button key={i} type="button" class={`movebutton type-${moveData.type||'Normal'}`} style="width:100%;min-height:76px;margin:0" disabled={disabled} aria-disabled={!!disabled} onClick={()=>{
								if(needsTarget && targets.length>1){room.cherishExtractionTarget={rqid:request.rqid,index,move:i};this.forceUpdate();}
								else choose(index,`move ${i+1}${needsTarget&&targets.length ? ` ${targets[0]!.value}` : ''}`);
							}}>
								{label}<br/><small class="type">{CHERISH_TYPE_KO[moveData.type] || moveData.type}</small> <small class="pp">{move.pp ?? '—'} / {move.maxpp ?? '—'} PP</small>
							</button>;
						})}
					</div>}
				</section>;
			})}
			{mine.length>0&&<button class="button" style="margin-top:8px" onClick={()=>{room.cherishExtractionTarget=null;send(`/undo |${request.rqid}`);this.forceUpdate();}}>내 선택 다시 고르기</button>}
		</div>;
	}
	// cherish-extraction-coop:end
	renderControls(overlayVersion = false, hidePlayerControls = false) {
		if ((this.props.room as any).cherishExtractionOwners && !this.props.room.battle?.ended) return hidePlayerControls ? null : this.renderExtractionCoop();
		if ((this.props.room as any).cherishLesson) return this.renderCherishLessonControls(); // cherish-lesson
		const room = this.props.room;
		if (!room.battle) return null;
		if (overlayVersion) {
			if (!room.side || !room.request || room.battle.ended) return null;
			const cherishOtherOverlay = this.cherishOtherTurn();
			if (cherishOtherOverlay) return this.renderCherishOtherTurn(cherishOtherOverlay);
			return this.renderPlayerControls(room.request, true);
		}
		if (room.battle.ended) return this.renderAfterBattleControls();
		if (room.side && room.request) {
			if (hidePlayerControls) return null;
			// cherish: 파티 난투전 — 내 아이가 아니면 지시 UI 대신 누구 차례인지 알린다
			const cherishOther = this.cherishOtherTurn();
			if (cherishOther) return this.renderCherishOtherTurn(cherishOther);
			return this.renderPlayerControls(room.request);
		}
		// cherish-bshell: 되감기 줄은 관전자만 (2026-09-07). |win| 수신 시 request 가 먼저 비고
		// battle.ended 는 연출이 끝나야 켜져서, 그 틈에 이 줄이 종료 카드 직전 번쩍였다.
		if (BattlePanel.cherishShellOn() && room.side) return null;
		if (room.battle.stepQueue.length === 0) return null;

		return <p class="cwt-live-status" role="status" style="margin:6px 0;font-size:12px;color:#718096">실시간 관전 중</p>; // cherish-playback-removed
	}
	// cherish-badge:begin (patch-effect-badge.py)
	cherishBadgesOn() {
		return !this.props.room.battle.cherishNoBadges;
	}
	cherishMoveBadges(tooltips: BattleTooltips, move: Dex.Move, value: ModifiableValue, forMaxMove?: boolean | Dex.Move) {
		if (!this.cherishBadgesOn()) return undefined;
		return tooltips.cherishMoveBadges(move, value, forMaxMove);
	}
	cherishSwitchBadges(serverPokemon: ServerPokemon) {
		const tooltips = (this.props.room.battle.scene as BattleScene).tooltips;
		if (!tooltips) return null;
		return tooltips.cherishSwitchBadges(serverPokemon);
	}
	static cherishEffClass(eff: number) {
		if (eff === 0) return 'x0';
		if (eff < 0.5) return 'x025';
		if (eff < 1) return 'x05';
		if (eff > 2) return 'x4';
		return 'x2';
	}
	/**
	 * 기술 버튼 모서리 배지 — 배열 순서 = 화면 자리 (cherishMoveBadges 참고).
	 * 싱글 [l] · 더블 [l, r] · 난투전 [l, r, bl, br] (아래 둘 중 내 자리는 null 이라 안 그려진다).
	 */
	renderCherishMoveBadges(badges?: readonly (number | null)[]) {
		if (!badges) return null;
		const pos = badges.length >= 4 ? ['l', 'r', 'bl', 'br'] : badges.length === 2 ? ['l', 'r'] : ['l'];
		return badges.map((eff, i) => {
			if (eff === null || eff === 1) return null;
			// 색은 배율이 아니라 **나에게 유리한가**로 (2026-09-05 사용자 결정): 2배 = 유리(파랑), 반감·무효 = 불리(빨강)
			return <span class={`cherish-eff cherish-eff-${pos[i] || 'l'} ${BattlePanel.cherishEffClass(eff)} ${eff > 1 ? 'good' : 'bad'}`}>
				{'\u00D7'}{eff}
			</span>;
		});
	}
	/** 교체 버튼 두 줄째: 피해(받는 배율) · 공격(내 공격기 최대 배율). ×1 은 생략 */
	renderCherishSwitchBadges(badges?: readonly [number | null, number | null] | null) {
		if (!badges) return null;
		const [taken, dealt] = badges;
		return <span class="cherish-sw">
			{/* 피해는 적게 받을수록 유리(파랑), 공격은 많이 줄수록 유리(파랑) — 배율 색이 아니라 유불리 색 */}
			{taken !== null && taken !== 1 && <span class={`cherish-eff ${BattlePanel.cherishEffClass(taken)} ${taken < 1 ? 'good' : 'bad'}`}>
				피해 {'\u00D7'}{taken}
			</span>}
			{dealt !== null && dealt !== 1 && <span class={`cherish-eff ${BattlePanel.cherishEffClass(dealt)} ${dealt > 1 ? 'good' : 'bad'}`}>
				공격 {'\u00D7'}{dealt}
			</span>}
		</span>;
	}
	// cherish-badge:end
	renderMoveButton(props: {
		name: string, cmd: string, type: Dex.TypeName, tags: string, tooltip: string,
		moveData: { pp?: number, maxpp?: number, disabled?: boolean },
		badges?: readonly (number | null)[], // cherish-badge
	} | null) {
		if (!props) {
			return <button class="movebutton" disabled>&nbsp;</button>;
		}
		const pp = props.moveData.maxpp ? `${props.moveData.pp!}/${props.moveData.maxpp}` : '\u2014';
		// cherish-world: 기술명 한글 (ko.js 공식 이름), 실패 시 영문 폴백
		const cherishMoveData = Dex.moves.get(props.name);
		const cherishMoveName = (cherishMoveData?.exists && (Dex.text.get(cherishMoveData) as any).name) || props.name;
		return <button
			data-cmd={props.cmd} data-tooltip={props.tooltip}
			class={`movebutton has-tooltip ${props.moveData.disabled ? 'disabled' : `type-${props.type}`}`}
			aria-disabled={props.moveData.disabled}
		>
			{this.renderCherishMoveBadges(props.badges)}
			{cherishMoveName}<br />
			<small class="type">{CHERISH_TYPE_KO[props.type] || props.type} <span class="effectiveness-icon">{props.badges ? '' : props.tags}</span></small> {}
			<small class="pp">{pp}</small>&nbsp;
		</button>;
	}
	// cherish-tpsprite: 선봉 선택 카드는 40x30 파티 아이콘 대신 gen5 96x96 배틀 스프라이트.
	// Dex.getSpriteData() 는 쓰지 않는다 — 애니 설정이 켜져 있으면 ani/*.webp(장당 130KB+)를
	// 돌려주는데 카드에는 정지 이미지가 맞다.
	// 폼 전용 파일이 없는 종(실측 2종: greninja-bond · rockruff-dusk)을 위해 기본 종 스프라이트를
	// 배경 **두 번째 레이어**로 깐다 — 앞 레이어가 404 면 뒤 레이어가 그대로 보인다.
	// cherish-hqart (2026-09-06): 카드 그림은 /cherish/sprite 320px 공식 일러스트 (앨범·임베드·개체 상세와 같은 그림).
	// gen5 96px 는 그림이 상자의 40% 라 카드에서 작아 보였다(사용자 지적). <img> 라서 404 면 onError 로 gen5 로 내려간다 —
	// 배경 다층 폴백은 일러스트의 투명 부분에 뒤 레이어가 비쳐서 못 쓴다. 이로치는 shiny/ 하위 (patch-mon-detail.py 와 같은 규칙).
	// cherish-form-card:begin (patch-custom-form.py)
	/**
	 * 이 카드가 그릴 개체의 커스텀 폼 id (없으면 null).
	 * 배틀 필드의 Pokemon 은 사이드 안 순번으로, 교체 메뉴의 ServerPokemon 은 `battle.myPokemon`
	 * 안 순번으로 찾는다 — 둘 다 서버가 내준 팀 순서와 같다.
	 */
	cherishFormIdOf(pokemon: Pokemon | ServerPokemon): string | null {
		const battle: any = this.props.room.battle;
		const map: any = battle?.cherishForms;
		if (!map) return null;
		const side: any = (pokemon as any).side;
		if (side?.sideid && Array.isArray(side.pokemon)) {
			const i = side.pokemon.indexOf(pokemon);
			const bySide = map[side.sideid];
			if (bySide && i >= 0 && typeof bySide[i] === 'string') return bySide[i];
			return null;
		}
		const mine: any[] = battle?.myPokemon || [];
		const j = mine.indexOf(pokemon);
		const mySideId = battle?.mySide?.sideid;
		const byMine = mySideId ? map[mySideId] : null;
		if (byMine && j >= 0 && typeof byMine[j] === 'string') return byMine[j];
		return null;
	}
	// cherish-form-card:end
	cherishCardArt(pokemon: Pokemon | ServerPokemon) {
		const species = Dex.species.get(toID((pokemon as any).speciesForme || pokemon.name));
		const shiny = !!(pokemon as any).shiny;
		// 커스텀 폼(2026-09-10)이 이로치를 덮는다. 자산이 없는 종이면 404 → onError 가 gen5 로 내려가는데,
		// 그 전에 보통 일러스트로 한 번 더 떨어지도록 폴백 사슬을 폼 → hq → gen5 로 잇는다.
		const cherishForm = this.cherishFormIdOf(pokemon);
		const hqBase = `/cherish/sprite/${shiny ? 'shiny/' : ''}${species.spriteid}.png?v=2`;
		const hq = cherishForm ? `/cherish/form/${cherishForm}/${species.spriteid}.png?v=1` : hqBase;
		const g5 = `${Dex.resourcePrefix}sprites/${shiny ? 'gen5-shiny' : 'gen5'}/${species.spriteid}.png`;
		const onError = (e: Event) => {
			const img = e.currentTarget as HTMLImageElement;
			// 폼 그림이 없으면 보통 일러스트로 한 단계, 그것도 없으면 gen5 로 (cherish-form-card)
			if (!img.dataset.fbForm && cherishForm) {
				img.dataset.fbForm = '1';
				img.src = hqBase;
				return;
			}
			if (img.dataset.fb) return;
			img.dataset.fb = '1';
			img.classList.add('cherish-tpart-g5');
			img.src = g5;
		};
		return <span class="cherish-tpart"><img src={hq} alt="" draggable={false} onError={onError} /></span>;
	}
	cherishTeamSpriteStyle(pokemon: Pokemon | ServerPokemon) {
		const species = Dex.species.get(toID((pokemon as any).speciesForme || pokemon.name));
		const dir = (pokemon as any).shiny ? 'gen5-shiny' : 'gen5';
		const layers = [`url(${Dex.resourcePrefix}sprites/${dir}/${species.spriteid}.png)`];
		const base = Dex.species.get(species.baseSpecies);
		if (base.spriteid && base.spriteid !== species.spriteid) {
			layers.push(`url(${Dex.resourcePrefix}sprites/${dir}/${base.spriteid}.png)`);
		}
		return `background-image:${layers.join(',')}`;
	}
	renderPokemonButton(props: {
		pokemon: Pokemon | ServerPokemon | null, cmd: string, noHPBar?: boolean, disabled?: boolean | 'fade', tooltip: string,
		badges?: readonly [number | null, number | null] | null, // cherish-badge
		bigSprite?: boolean, // cherish-tpsprite: 선봉 선택 카드만 true
	}) {
		const pokemon = props.pokemon;
		if (!pokemon) {
			return <button
				data-cmd={props.cmd} class={`${props.disabled ? 'disabled ' : ''}has-tooltip`}
				aria-disabled={props.disabled}
				style={props.disabled === 'fade' ? 'opacity: 0.5' : ''} data-tooltip={props.tooltip}
			>
				(빈 자리)
			</button>;
		}

		let hpColorClass;
		switch (BattleScene.getHPColor(pokemon)) {
		case 'y': hpColorClass = 'hpbar hpbar-yellow'; break;
		case 'r': hpColorClass = 'hpbar hpbar-red'; break;
		default: hpColorClass = 'hpbar'; break;
		}

		return <button
			data-cmd={props.cmd} class={`${props.disabled ? 'disabled ' : ''}${pokemon.fainted ? 'cherish-fainted ' : ''}has-tooltip`}
			aria-disabled={props.disabled}
			style={props.disabled === 'fade' ? 'opacity: 0.5' : ''} data-tooltip={props.tooltip}
		>
			{props.bigSprite ?
				this.cherishCardArt(pokemon) : // cherish-hqart
				PSIcon({ pokemon })}
			<span class="cherish-tpname">{pokemon.name}</span>
			{this.renderCherishSwitchBadges(props.badges)}
			{
				!props.noHPBar && !pokemon.fainted &&
				<span class={hpColorClass}>
					<span style={{ width: `${Math.max(1, Math.round(pokemon.hp * 100 / pokemon.maxhp))}%` }}></span>{/* cherish-bshell: 퍼센트 폭 */}
				</span>
			}
			{!props.noHPBar && pokemon.status && <span class={`status ${pokemon.status}`}></span>}
		</button>;
	}
	renderMoveMenu(choices: BattleChoiceBuilder, overlayVersion?: boolean) {
		const moveRequest = choices.currentMoveRequest()!;

		const canDynamax = moveRequest.canDynamax && !choices.alreadyMax;
		const canMegaEvo = moveRequest.canMegaEvo && !choices.alreadyMega;
		const canMegaEvoX = moveRequest.canMegaEvoX && !choices.alreadyMega;
		const canMegaEvoY = moveRequest.canMegaEvoY && !choices.alreadyMega;
		const canZMove = moveRequest.zMoves && !choices.alreadyZ;
		const canUltraBurst = moveRequest.canUltraBurst;
		const canTerastallize = moveRequest.canTerastallize;

		const maybeDisabled = moveRequest.maybeDisabled;
		const maybeLocked = moveRequest.maybeLocked;

		return <div class="movemenu">
			{maybeDisabled && <p><em class="movewarning">
				기술 일부가 잠겨 있을 수 있어요. 그러면 공격을 고른 뒤 무를 수 없어요!
			</em></p>}
			{maybeLocked && <p><em class="movewarning">
				같은 기술만 쓰도록 묶여 있을 수 있어요. {}
				<button class="button" data-cmd="/choose testfight">눌러서 확인하기</button> {}
				(묶여 있으면 교체가 막혀요)
			</em></p>}
			{!overlayVersion && this.renderMoveControls(moveRequest, choices)}
			<div class="megaevo-box">
				{canDynamax && <label class={`megaevo${choices.current.max ? ' cur' : ''}`}>
					<input type="checkbox" name="max" checked={choices.current.max} onChange={this.toggleBoostedMove} /> {}
					{moveRequest.gigantamax ? 'Gigantamax' : 'Dynamax'}
				</label>}
				{canMegaEvo && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`}>
					<input type="checkbox" name="mega" checked={choices.current.mega} onChange={this.toggleBoostedMove} /> {}
					Mega Evolution
				</label>}
				{canMegaEvoX && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`}>
					<input type="checkbox" name="megax" checked={choices.current.megax} onChange={this.toggleBoostedMove} /> {}
					Mega Evolution X
				</label>}
				{canMegaEvoY && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`}>
					<input type="checkbox" name="megay" checked={choices.current.megay} onChange={this.toggleBoostedMove} /> {}
					Mega Evolution Y
				</label>}
				{canUltraBurst && <label class={`megaevo${choices.current.ultra ? ' cur' : ''}`}>
					<input type="checkbox" name="ultra" checked={choices.current.ultra} onChange={this.toggleBoostedMove} /> {}
					Ultra Burst
				</label>}
				{canZMove && <label class={`megaevo${choices.current.z ? ' cur' : ''}`}>
					<input type="checkbox" name="z" checked={choices.current.z} onChange={this.toggleBoostedMove} /> {}
					Z-Power
				</label>}
				{canTerastallize && <label class={`megaevo${choices.current.tera ? ' cur' : ''}`}>
					<input type="checkbox" name="tera" checked={choices.current.tera} onChange={this.toggleBoostedMove} /> {}
					Tera {PSIcon({ type: canTerastallize, new: true, tera: true })}
				</label>}
			</div>
			{overlayVersion && this.renderMoveControls(moveRequest, choices)}
		</div>;
	}
	renderMoveControls(active: BattleRequestActivePokemon, choices: BattleChoiceBuilder) {
		const battle = this.props.room.battle;
		const dex = battle.dex;
		const pokemonIndex = choices.index();
		const activeIndex = battle.mySide.n > 1 ? pokemonIndex + battle.pokemonControlled : pokemonIndex;
		const serverPokemon = choices.request.side!.pokemon[pokemonIndex];
		const valueTracker = new ModifiableValue(battle, battle.nearSide.active[activeIndex]!, serverPokemon);
		const tooltips = (battle.scene as BattleScene).tooltips;

		if (choices.current.max || (active.maxMoves && !active.canDynamax)) {
			if (!active.maxMoves) {
				return <div class="message-error">다이맥스했는데 쓸 수 있는 기술이 없어요</div>;
			}
			const gmax = active.gigantamax && dex.moves.get(active.gigantamax);
			return active.moves.map((moveData, i) => {
				const move = dex.moves.get(moveData.name);
				const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker, gmax || true);
				let maxMoveData: { name: string, id: ID } = active.maxMoves![i];
				if (maxMoveData.name !== 'Max Guard') {
					maxMoveData = tooltips.getMaxMoveFromType(moveType, gmax);
				}
				const gmaxTooltip = maxMoveData.id.startsWith('gmax') ? `|${maxMoveData.id}` : ``;
				const tooltip = `maxmove|${moveData.name}|${pokemonIndex}${gmaxTooltip}`;
				return this.renderMoveButton({
					name: maxMoveData.name,
					cmd: `/move ${i + 1} max`,
					type: moveType,
					tags,
					tooltip,
					moveData,
					badges: this.cherishMoveBadges(tooltips, move, valueTracker, gmax || true),
				});
			});
		}

		if (choices.current.z) {
			if (!active.zMoves) {
				return <div class="message-error">쓸 수 있는 Z기술이 없어요</div>;
			}
			return active.moves.map((moveData, i) => {
				const zMoveData = active.zMoves![i];
				if (!zMoveData) {
					return this.renderMoveButton(null);
				}
				const specialMove = dex.moves.get(zMoveData.name);
				const move = specialMove.exists ? specialMove : dex.moves.get(moveData.name);
				const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker);
				const tooltip = `zmove|${moveData.name}|${pokemonIndex}`;
				return this.renderMoveButton({
					name: zMoveData.name,
					cmd: `/move ${i + 1} zmove`,
					type: moveType,
					tags,
					tooltip,
					moveData: { pp: 1, maxpp: 1 },
					badges: this.cherishMoveBadges(tooltips, move, valueTracker),
				});
			});
		}

		const special = choices.moveSpecial(choices.current);
		return active.moves.map((moveData, i) => {
			const move = dex.moves.get(moveData.name);
			const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker);
			const tooltip = `move|${moveData.name}|${pokemonIndex}`;
			return this.renderMoveButton({
				name: move.name,
				cmd: `/move ${i + 1}${special}`,
				type: moveType,
				tags,
				tooltip,
				moveData,
				badges: this.cherishMoveBadges(tooltips, move, valueTracker),
			});
		});
	}
	renderMoveTargetControls(request: BattleMoveRequest, choices: BattleChoiceBuilder) {
		const battle = this.props.room.battle;
		let moveTarget = choices.currentMove()?.target;
		if ((moveTarget === 'adjacentAlly' || moveTarget === 'adjacentFoe') && battle.gameType === 'freeforall') {
			moveTarget = 'normal';
		}
		const moveChoice = choices.stringChoice(choices.current);

		const userSlot = choices.index() + Math.floor(battle.mySide.n / 2) * battle.pokemonControlled;
		const userSlotCross = battle.farSide.active.length - 1 - userSlot;

		return <>
			{battle.farSide.active.map((pokemon, i) => {
				let disabled = false;
				if (moveTarget === 'adjacentAlly' || moveTarget === 'adjacentAllyOrSelf') {
					disabled = true;
				} else if (moveTarget === 'normal' || moveTarget === 'adjacentFoe') {
					if (Math.abs(userSlotCross - i) > 1) disabled = true;
				}

				if (pokemon?.fainted) pokemon = null;
				return this.renderPokemonButton({
					pokemon,
					cmd: disabled ? `` : `/${moveChoice} +${i + 1}`,
					disabled: disabled && 'fade',
					tooltip: `activepokemon|1|${i}`,
				});
			}).reverse()}
			<div style={{ clear: 'left' }}></div>
			{battle.nearSide.active.map((pokemon, i) => {
				let disabled = false;
				if (moveTarget === 'adjacentFoe') {
					disabled = true;
				} else if (moveTarget === 'normal' || moveTarget === 'adjacentAlly' || moveTarget === 'adjacentAllyOrSelf') {
					if (Math.abs(userSlot - i) > 1) disabled = true;
				}
				if (moveTarget !== 'adjacentAllyOrSelf' && userSlot === i) disabled = true;

				if (pokemon?.fainted) pokemon = null;
				return this.renderPokemonButton({
					pokemon,
					cmd: disabled ? `` : `/${moveChoice} -${i + 1}`,
					disabled: disabled && 'fade',
					tooltip: `activepokemon|0|${i}`,
				});
			})}
		</>;
	}
	renderSwitchMenu(
		request: BattleMoveRequest | BattleSwitchRequest, choices: BattleChoiceBuilder, ignoreTrapping?: boolean
	) {
		const numActive = choices.requestLength();
		const maybeTrapped = !ignoreTrapping && choices.currentMoveRequest()?.maybeTrapped;
		const trapped = !ignoreTrapping && !maybeTrapped && choices.currentMoveRequest()?.trapped;
		const isReviving = choices.isReviving();

		const cherishSw = this.cherishBadgesOn(); // cherish-badge
		return <div class={`switchmenu cherish-tp cherish-swcards${cherishSw ? ' cherish-sw' : ''}`}>
			{maybeTrapped && <em class="movewarning">
				<strong>붙잡혀 있을지도</strong> 몰라요! 교체 선택을 취소할 수 없어요!<br />
			</em>}
			{trapped && <em class="movewarning">
				<strong>붙잡혀서</strong> 교체할 수 없어요!<br />
			</em>}
			{isReviving && <em class="movewarning">
				부활시킬 포켓몬을 골라주세요!<br />
			</em>}
			{request.side.pokemon.map((serverPokemon, i) => {
				let cantSwitch = trapped || i < numActive || choices.alreadySwitchingIn.includes(i + 1) || serverPokemon.fainted;
				if (isReviving) cantSwitch = !serverPokemon.fainted || choices.alreadySwitchingIn.includes(i + 1);
				return this.renderPokemonButton({
					pokemon: serverPokemon,
					cmd: `/switch ${i + 1}`,
					disabled: cantSwitch,
					tooltip: `switchpokemon|${i}`,
					badges: cherishSw ? this.cherishSwitchBadges(serverPokemon) : null,
					bigSprite: true, // cherish-swcards
				});
			})}
			{request.ally?.pokemon?.map((serverPokemon, i) => {
				return this.renderPokemonButton({
					pokemon: serverPokemon,
					cmd: `/switch notMine`,
					disabled: true,
					tooltip: `allypokemon|${i}`,
					bigSprite: true, // cherish-swcards
				});
			})}
		</div>;
	}
	renderTeamPreviewChooser(
		request: | BattleTeamRequest, choices: BattleChoiceBuilder, bigSprite = false
	) {
		return request.side.pokemon.map((serverPokemon, i) => {
			const cantSwitch = choices.alreadySwitchingIn.includes(i + 1);
			return this.renderPokemonButton({
				pokemon: serverPokemon,
				cmd: `/switch ${i + 1}`,
				disabled: cantSwitch && 'fade',
				tooltip: `switchpokemon|${i}`,
				bigSprite,
			});
		});
	}
	renderTeamList(overlayVersion = false) {
		const team = this.team;
		if (!team) return;
		return <div class="switchcontrols">
			{!overlayVersion && <h3 class="switchselect">내 팀</h3>}
			<div class="switchmenu cherish-tp cherish-swcards cherish-teamlist">
				{team.map((serverPokemon, i) => {
					return this.renderPokemonButton({
						pokemon: serverPokemon,
						cmd: "",
						disabled: true,
						tooltip: `switchpokemon|${i}`,
						bigSprite: true, // cherish-teamlist
					});
				})}
			</div>
		</div>;
	}
	renderChosenTeam(request: BattleTeamRequest, choices: BattleChoiceBuilder, bigSprite = false) {
		return choices.alreadySwitchingIn.map(slot => {
			const serverPokemon = request.side.pokemon[slot - 1];
			return this.renderPokemonButton({
				pokemon: serverPokemon,
				cmd: `/switch ${slot}`,
				tooltip: `switchpokemon|${slot - 1}`,
				bigSprite,
			});
		});
	}
	renderOldChoices(request: BattleRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		if (!choices) return null; // should not happen
		if (
			(request.requestType !== 'move' && request.requestType !== 'switch' && request.requestType !== 'team') ||
			choices.isEmpty()
		) {
			return null;
		}

		let buf: preact.ComponentChild[] = [
			<button data-cmd="/cancelone" class="button"><i class="fa fa-chevron-left" aria-hidden></i> 뒤로</button>, ' ',
		];
		if (choices.isDone() && (
			choices.noCancel || this.props.room.battle.hardcoreMode ||
			(choices.choices.length <= 1 && !overlayVersion)
		)) {
			buf = [];
		}

		if (choices.serializedChoice) {
			if (choices.serializedChoice === 'default') {
				return [`자동 선택`, <br />];
			}
			return [`서버가 보낸 선택을 알 수 없어요: `, <code>{choices.serializedChoice}</code>, <br />];
		}

		const battle = this.props.room.battle;
		let teamList = false;
		for (let i = 0; i < choices.choices.length; i++) {
			const choiceString = choices.choices[i];
			if (choiceString === "testfight") {
				buf.push(`${request.side.pokemon[i].name}: 기술이 고정돼 있어요.`);
				return buf;
			}
			let choice;
			try {
				choice = choices.parseChoice(choiceString, i);
			} catch (err: any) {
				buf.push(<span class="message-error">{err.message}</span>);
			}
			if (!choice) continue;
			const pokemon = request.side.pokemon[i];
			const active = request.requestType === 'move' ? request.active[i] : null;
			if (choice.choiceType === 'move') {
				buf.push(`${pokemon.name}: `);
				if (choice.mega) buf.push(<strong>메가진화</strong>, ` 후 `);
				if (choice.megax) buf.push(<strong>메가진화(X)</strong>, ` 후 `);
				if (choice.megay) buf.push(<strong>메가진화(Y)</strong>, ` 후 `);
				if (choice.ultra) buf.push(<strong>울트라버스트</strong>, ` 후 `);
				if (choice.tera) buf.push(`테라스탈 (`, <strong>{active?.canTerastallize || '???'}</strong>, `) 후 `);
				if (choice.max && active?.canDynamax) buf.push(active?.gigantamax ? `거다이맥스 후 ` : `다이맥스 후 `);
				// cherish-bshell: 기술명 한글 (기술 버튼과 같은 경로)
				const cherishOcMove = choices.currentMove(choice, i);
				const cherishOcData = cherishOcMove ? Dex.moves.get(cherishOcMove.name) : null;
				buf.push(<strong>{(cherishOcData?.exists && (Dex.text.get(cherishOcData) as any).name) || cherishOcMove?.name}</strong>);
				if (choice.targetLoc > 0) {
					const target = battle.farSide.active[choice.targetLoc - 1];
					if (!target) {
						buf.push(` → ${choice.targetLoc}번 자리`);
					} else {
						buf.push(` → ${target.name}`);
					}
				} else if (choice.targetLoc < 0) {
					const target = battle.nearSide.active[-choice.targetLoc - 1];
					const ally = battle.gameType !== 'freeforall' ? '아군' : '';
					if (!target) {
						buf.push(` → ${ally} ${choice.targetLoc}번 자리`);
					} else {
						buf.push(` → ${ally} ${target.name}`);
					}
				}
				buf.push(`.`);
			} else if (choice.choiceType === 'switch') {
				const target = request.side.pokemon[choice.targetPokemon - 1];
				if (choices.isReviving(i)) {
					buf.push(`${pokemon.name} 대신 `, <strong>{target.name}</strong>, ` 부활.`);
				} else {
					buf.push(`${pokemon.name} 대신 `, <strong>{target.name}</strong>, ` 교체.`);
				}
			} else if (choice.choiceType === 'shift') {
				buf.push(`${pokemon.name} `, <strong>중앙</strong>, `으로 이동.`);
			} else if (choice.choiceType === 'team') {
				const target = request.side.pokemon[choice.targetPokemon - 1];
				buf.push(teamList ? `, ` : `고른 포켓몬: `, <strong>{target.name}</strong>);
				teamList = true;
			}
			if (!teamList) buf.push(<br />);
		}
		if (teamList) {
			buf.push('.', <br />);
		}
		return buf;
	}
	overlayControlClass(overlay: 'move' | 'switch') {
		return `button ${overlay}-button${this.props.room.overlayActive === overlay ? ' cur' : ''}`;
	}
	renderPlayerAnimationControls(overlayVersion = false) {
		const room = this.props.room;
		if (overlayVersion) {
			const canSkip = !room.battle.hardcoreMode;
			return <>
				{canSkip && <div class="overlay-controls-skip">
					<button class="button" data-cmd="/ffto end"><i class="fa fa-fast-forward" aria-hidden></i><br />건너뛰기</button>
				</div>}
			</>;
		}
		return <div class="inline-controls">
			{!room.battle.hardcoreMode && !BattlePanel.cherishShellOn() && <div class="whatdo" style="padding-bottom:0">
				<button class="button" data-cmd="/ffto end"><i class="fa fa-fast-forward" aria-hidden></i><br />연출 건너뛰기</button>
			</div>}
			{this.renderTeamList()}
		</div>;
	}
	renderPlayerMoveControls(request: BattleMoveRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const room = this.props.room;
		const index = choices.index();
		const pokemon = request.side.pokemon[index];

		if (choices.current.move) {
			const cherishMoveRaw = choices.currentMove()?.name;
			// cherish5: 기술 버튼(renderMoveButton)과 같은 경로로 공식 한글명을 찾는다 — 코드에 이름을 박지 않는다
			const cherishMoveData = cherishMoveRaw ? Dex.moves.get(cherishMoveRaw) : null;
			const moveName = (cherishMoveData?.exists && (Dex.text.get(cherishMoveData) as any).name) || cherishMoveRaw;
			if (overlayVersion) {
				return <>
					<div class="overlay-controls-list">
						<button class="button move-button cur"><strong>기술</strong></button> {}
						<button class="button switch-button disabled"><strong>교체</strong></button>
					</div>
					<div class="targetcontrols">
						<p class="overlay-message">
							{this.renderOldChoices(request, choices, true)}
							{pokemon.name}의 <strong>{moveName}</strong>, 누구에게?
						</p>
						<div class="switchmenu">
							{this.renderMoveTargetControls(request, choices)}
						</div>
					</div>
				</>;
			}
			return <div class="inline-controls">
				<div class="whatdo">
					{this.renderOldChoices(request, choices)}
					{pokemon.name}의 <strong>{moveName}</strong>, 누구에게? {}
				</div>
				<div class="switchcontrols">
					<div class="switchmenu">
						{this.renderMoveTargetControls(request, choices)}
					</div>
				</div>
			</div>;
		}

		const canShift = room.battle.gameType === 'triples' && index !== 1;

		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class={this.overlayControlClass('move')} data-cmd="/movemenu"><strong>기술</strong></button> {}
					<button class={this.overlayControlClass('switch')} data-cmd="/switchmenu"><strong>교체</strong></button>
				</div>
				{!room.overlayActive && <div class="whatdo">
					{this.renderOldChoices(request, choices, true)}
					<strong>{pokemon.name}</strong>, 어떻게 할까?
				</div>}
				{room.overlayActive === 'move' && <div class="movecontrols">
					{this.renderMoveMenu(choices, true)}
				</div>}
				{room.overlayActive === 'switch' && <div class="switchcontrols">
					{canShift && (
						<button data-cmd="/shift">가운데로 이동</button>
					)}
					{this.renderSwitchMenu(request, choices)}
				</div>}
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{this.renderOldChoices(request, choices)}
				<strong>{pokemon.name}</strong>, 어떻게 할까?
			</div>
			<div class="movecontrols">
				<h3 class="moveselect">기술</h3>
				{this.renderMoveMenu(choices)}
			</div>
			<div class="switchcontrols">
				{canShift && [
					<h3 class="shiftselect">자리 이동</h3>,
					<button data-cmd="/shift">가운데로 이동</button>,
				]}
				<h3 class="switchselect">교체</h3>
				{this.renderSwitchMenu(request, choices)}
			</div>
		</div>;
	}
	renderPlayerSwitchControls(request: BattleSwitchRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const pokemon = request.side.pokemon[choices.index()];
		const prompt = choices.isReviving() ?
			<>부활시킬 포켓몬을 골라주세요!</> :
			<><strong>{pokemon.name}</strong> 대신 나갈 포켓몬은?</>;
		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class="button switch-button cur"><strong>교체</strong></button>
				</div>
				<div class="switchcontrols">
					<p class="overlay-message">
						{this.renderOldChoices(request, choices, true)}
						{prompt}
					</p>
					{this.renderSwitchMenu(request, choices, true)}
				</div>
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{this.renderOldChoices(request, choices)}
				{prompt}
			</div>
			<div class="switchcontrols">
				<h3 class="switchselect">교체</h3>
				{this.renderSwitchMenu(request, choices, true)}
			</div>
		</div>;
	}
	renderPlayerTeamPreviewControls(request: BattleTeamRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const prompt = choices.alreadySwitchingIn.length > 0 ? (
			[<button data-cmd="/cancelone" class="button"><i class="fa fa-chevron-left" aria-hidden></i> 뒤로</button>,
				" 나머지 순서도 정해주세요! "]
		) : (
			"어떤 포켓몬으로 시작할까요? "
		);
		const chosenTeamSizeLabel = (request.chosenTeamSize || 0) > 1 ? ` / ${request.chosenTeamSize!}` : '';
		const chooseLabel = choices.alreadySwitchingIn.length <= 0 ?
			`선봉${chosenTeamSizeLabel}` : `${choices.alreadySwitchingIn.length + 1}번째${chosenTeamSizeLabel}`;
		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class="button switch-button cur"><strong>팀</strong></button>
				</div>
				<div class="teamcontrols">
					<p class="overlay-message">{prompt}</p>
					<h3 class="switchselect">{chooseLabel} 선택</h3>
					<div class="switchmenu">
						{this.renderTeamPreviewChooser(request, choices)}
						<div style="clear:left"></div>
					</div>
					{choices.alreadySwitchingIn.length > 0 && <>
						<h3 class="switchselect">지금까지 고른 팀</h3>
						<div class="switchmenu">
							{this.renderChosenTeam(request, choices)}
						</div>
					</>}
				</div>
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{prompt}
			</div>
			<div class="switchcontrols">
				<h3 class="switchselect">
					{chooseLabel} 선택
				</h3>
				<div class="switchmenu cherish-tp">
					{this.renderTeamPreviewChooser(request, choices, true)}
					<div style="clear:left"></div>
				</div>
			</div>
			<div class="switchcontrols">
				{choices.alreadySwitchingIn.length > 0 && <h3 class="switchselect">지금까지 고른 팀</h3>}
				<div class="switchmenu cherish-tp">
					{this.renderChosenTeam(request, choices, true)}
				</div>
			</div>
		</div>;
	}
	renderPlayerControls(request: BattleRequest, overlayVersion = false) {
		const room = this.props.room;
		const atEnd = room.battle.atQueueEnd;
		if (!atEnd) return this.renderPlayerAnimationControls(overlayVersion);

		let choices = room.choices;
		if (!choices) return 'Error: Missing BattleChoiceBuilder';
		if (choices.request !== request) {
			choices = new BattleChoiceBuilder(request);
			room.choices = choices;
			room.overlayActive = null;
		}

		if (choices.isDone()) {
			if (overlayVersion) {
				return <>
					<div class="overlay-controls-list">
						<button class={this.overlayControlClass('switch')} data-cmd="/switchmenu"><strong>팀</strong></button>
					</div>
					{!room.overlayActive && <div class="whatdo">
						{this.renderOldChoices(request, choices, true)}
					</div>}
					{room.overlayActive === 'switch' && this.renderTeamList(true)}
				</>;
			}
			return <div class="inline-controls">
				<div class="whatdo">
					{this.renderOldChoices(request, choices)}
					<em>상대를 기다리는 중...</em> {choices.noCancel || room.battle.hardcoreMode ?
						null : <button data-cmd="/cancel" class="button">취소</button>}
				</div>
				{this.renderTeamList()}
			</div>;
		}
		if (request.side) {
			room.battle.myPokemon = request.side.pokemon;
			this.team = request.side.pokemon;
		}
		switch (request.requestType) {
		case 'move':
			return this.renderPlayerMoveControls(request, choices, overlayVersion);
		case 'switch':
			return this.renderPlayerSwitchControls(request, choices, overlayVersion);
		case 'team':
			return this.renderPlayerTeamPreviewControls(request, choices, overlayVersion);
		}
		return null;
	}

	// cherish-avoid:begin (patch-avoid-ui.py)
	/**
	 * NPC(체리학교*·담임체리·체리쉬타워지킴이)는 회피 대상이 아니다 — 디스코드 계정 매핑이 없어
	 * api 가 404 「그 트레이너를 찾지 못했어요」로 거절한다. 눌러도 오류만 나던 걸 아예 안 그린다
	 * (2026-09-07, 체리쉬타워 종료 화면에서 발견). 판정 규칙은 patch-npc-hide.py 와 같다 —
	 * **NPC 계정을 늘리면 여기도 같이 늘릴 것.**
	 */
	cherishIsNpcFoe(id: string) {
		return !!id && (id.startsWith('체리학교') || id === '담임체리' || id === '체리쉬타워지킴이' || id === '체육관지킴이');
	}
	/** 버튼 노출 조건 — 마커 블록 안이라 조건이 늘어도 이미 적용된 서버에 반영된다. */
	cherishCanAvoidFoe() {
		const battle = this.props.room.battle;
		const foe = battle?.farSide;
		if (battle?.gameType !== 'singles' || !foe?.id || foe.id === PS.user.userid) return false;
		// 체리쉬타워 판은 상대 좌석이 고스트 주인 이름(별칭)으로 앉아 있어(patch-npc-alias.py) id 로는 NPC 인지 모른다.
		// 시작 배너(|uhtml|cherishdash| 「체리쉬타워 N층 · …」)가 있으면 탑 판 — 회피 대상이 아니다 (2026-09-07).
		const cherishQueue: string[] = (battle as any)?.stepQueue || [];
		if (cherishQueue.some(l => typeof l === 'string' && l.startsWith('|uhtml|cherishdash|') && l.includes('체리쉬타워 '))) return false;
		// 담임 체리는 거리두기에 못 담는다 (2026-10-26 사용자 결정). api 가 같은 목록으로 거절한다 —
		// 정본은 apps/api/src/lib/showdown-avoid.ts 의 UNAVOIDABLE_BOT_USERIDS.
		if (foe.id === '체리') return false;
		return !this.cherishIsNpcFoe(foe.id);
	}
	/**
	 * 2대2 「다음부터 같은 팀 안 하기」 (2026-09-17).
	 *
	 * gameType 'multi' 가 곧 2대2 다 (난투전은 'freeforall', 1대1 은 'singles').
	 * ⚠️ 같은 편이 누구였는지 **클라가 골라 보내지 않는다** — 방 id 만 보내고 서버가 기록에서
	 *    찾는다. 미궁 avoid-mate 와 같은 규약(받아 쓰면 아무나 남의 목록을 조작할 수 있다).
	 * 상대편은 언제나 봇 둘이므로 **같은 편의 다른 자리는 항상 사람**이다 — NPC 판정이 필요 없다.
	 */
	cherishCanAvoidAlly() {
		return this.props.room.battle?.gameType === 'multi' && !!PS.user.named;
	}
	cherishAvoidAlly = () => {
		if (!window.confirm(`같은 편이었던 트레이너와 다음 2대2 자동매칭부터 팀이 되지 않게 할까요?\n(최대 2명, 넘치면 오래된 상대부터 빠져요. 1대1·난투전에는 적용되지 않아요.)`)) return;
		fetch('https://cherish-world.com/api/showdown/avoids/ally', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ roomId: this.props.room.id }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) { PS.alert(json?.message || '등록에 실패했어요.'); return; }
			const d = json.data;
			PS.alert(d.already ?
				`${d.label} 님과는 이미 같은 팀이 안 되게 해 뒀어요.` :
				`다음 팀배틀 자동매칭부터 ${d.label} 님과 같은 팀이 되지 않아요.` + (d.evicted ? `\n(${d.evicted} 님은 목록에서 빠졌어요)` : '') +
				`\n「내 포켓몬 › 거리두기」에서 해제할 수 있어요.`);
		}).catch(() => PS.alert('등록에 실패했어요.'));
	};
	cherishAvoidFoe = () => {
		const battle = this.props.room.battle;
		const foe = battle?.farSide;
		if (!foe?.id || foe.id === PS.user.userid || this.cherishIsNpcFoe(foe.id)) return;
		if (!window.confirm(`${foe.name} 님을 다음 자동매칭부터 피할까요?\n(최대 2명, 넘치면 오래된 상대부터 빠져요. 3대3·6대6 에만 걸리고 팀배틀·대기방에는 적용되지 않아요.)`)) return;
		fetch('https://cherish-world.com/api/showdown/avoids', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userid: foe.id }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) { PS.alert(json?.message || '회피 등록에 실패했어요.'); return; }
			const d = json.data;
			PS.alert(d.already ?
				`${d.label} 님은 이미 회피 목록에 있어요.` :
				`다음 자동매칭부터 ${d.label} 님을 피해요.` + (d.evicted ? `\n(${d.evicted} 님은 목록에서 빠졌어요)` : '') +
				`\n「내 포켓몬 › 거리두기」에서 해제할 수 있어요.`);
		}).catch(() => PS.alert('회피 등록에 실패했어요.'));
	};
	// cherish-avoid:end
	// cherish-after-row:begin (patch-after-battle-row.py)
	renderCherishRoomButtons() {
		const room = this.props.room;
		return room.side ? (
				<>
					<button class="button" data-cmd="/close">
						<strong>메인 메뉴</strong><br /><small>(배틀 닫기)</small>
					</button> {}
					{/* cherish-challenge-off: 리매치 제거 (2026-09-05).
					    도전이 없어졌다. 다시 붙고 싶으면 홈에서 자동매칭을 돌리면 된다 —
					    그쪽만 Elo·오늘의 배틀 점수가 붙는다 (0284). */}
					{this.cherishCanAvoidFoe() &&
						<button class="button" onClick={this.cherishAvoidFoe}>
							<strong>🔕 이 트레이너와 거리두기</strong><br /><small>(자동매칭)</small>
						</button>}
					{this.cherishCanAvoidAlly() &&
						<button class="button" onClick={this.cherishAvoidAlly}>
							<strong>🔕 다음부터 같은 팀 안 하기</strong><br /><small>(2대2 자동매칭)</small>
						</button>}
				</>
			) : (
				<>
					{/* cherish-after-row v2: 관전자(끝난 배틀에 재진입한 참가자 포함)도 닫을 수 있게 */}
					<button class="button" data-cmd="/close">
						<strong>메인 메뉴</strong><br /><small>(배틀 닫기)</small>
					</button> {}
					<button class="button" data-cmd="/switchsides"><i class="fa fa-random" aria-hidden></i> 시점 전환</button> {}
					{!room.battle.hardcoreMode && <button class="button" data-cmd="/ffto">
						<i class="fa fa-random" aria-hidden></i> 턴 이동
					</button>}
				</>
			);
	}
	// cherish-after-row:end
	// cherish-tour-end:begin (patch-tournament-panel.py)
	renderCherishTourCommentary() {
		return <CherishTourStrip room={this.props.room} commentary />;
	}
	/**
	 * 종료 카드의 주 버튼 — **대회 판에서만** 「메인 메뉴」 자리를 대진표가 대신한다.
	 * 대회 판이 아니면 null 이라 호출부가 원래 버튼 줄을 그대로 그린다.
	 */
	renderCherishTourEnd() {
		const room = this.props.room;
		if (!cherishTourHasRoom(room.id)) return null;
		// 대진표를 먼저 열고 나서 닫는다. 닫은 방이 서버 메시지로 되살아나 홈으로 떨어지는 것까지
		// 막는 일은 cherish-tournament.tsx 가 한다 (2026-09-28).
		return <button class="button" onClick={() => CherishTourStrip.returnToBracket(room.id)}>
			<strong>대진표로 돌아가기</strong><br /><small>(배틀 닫기)</small>
		</button>;
	}
	// cherish-tour-end:end
	// cherish-team-chat:begin (patch-team-chat-ui.py)
	/**
	 * 2대2 같은 편 팀 채팅 띠 — **조작부 카드의 형제**로 그린다 (2026-09-22).
	 *
	 * 참가자가 아니면(관전·리플레이) 아예 안 부르고, 2대2 가 아니면 컴포넌트가 스스로 null 을
	 * 그린다(서버가 「열어도 된다」고 답해야 켜진다). 봇이 팀원이어도 분기 없이 똑같이 연다.
	 */
	renderCherishTeamChat(width: number) {
		const room = this.props.room;
		if (!room.side) return null;
		return <CherishTeamChat room={room} width={width} />;
	}
	// cherish-team-chat:end
	// cherish-raid-live-end:begin (patch-raid-live.py)
	/**
	 * 종료 카드의 주 버튼 — **마왕토벌전(실시간) 판에서만** 「메인 메뉴」 자리를 마왕전 화면이 대신한다 (2026-11-03).
	 * 대회 판(cherishTourHasRoom)은 대진표 버튼이 먼저 잡고, 그 밖엔 null 이라 호출부가 원래 버튼 줄을 그린다.
	 */
	renderCherishRaidLiveEnd() {
		const room = this.props.room;
		if (!/^battle-gen9cherishraidbattle-/.test(room.id)) return null;
		if (typeof (window as any).cherishTourHasRoom === 'function' && (window as any).cherishTourHasRoom(room.id)) return null;
		if (typeof (window as any).cwrlReturn !== 'function') return null;
		return <span><button class="button" onClick={() => (window as any).cwrlReturn(room.id)}>
			<strong>마왕토벌전으로 돌아가기</strong><br /><small>(배틀 닫기)</small>
		</button>{(window as any).cwrlAfterBattleChoices?.(room.id)}</span>;
	}
	// cherish-raid-live-end:end
	renderAfterBattleControls() {
		const room = this.props.room;
		if (BattlePanel.cherishShellOn()) return this.renderCherishEndCard(); // cherish-bshell
		const isNotTiny = room.width > 700;
		return <div class="inline-controls">
			<p>
				<button class="button" data-cmd="/play" style="min-width:4.5em">
					<i class="fa fa-undo" aria-hidden></i><br />다시보기
				</button> {}
				{isNotTiny && !room.battle.hardcoreMode && <>
					<button class="button button-first" data-cmd="/ffto 0" style="margin-right:2px">
						<i class="fa fa-undo" aria-hidden></i><br />첫 턴
					</button>
					<button class="button button-first" data-cmd="/ffto -1">
						<i class="fa fa-step-backward" aria-hidden></i><br />이전 턴
					</button>
				</>}
				{!isNotTiny && this.renderCherishRoomButtons()}
			</p>
			{/* cherish-after-row: 방 조작 버튼은 renderCherishRoomButtons() (2026-09-05).
			    좁은 화면에선 위 <p> 안에 이어 그리고, 넓은 화면만 둘째 줄로 내린다. */}
			{isNotTiny && <p>{this.renderCherishRoomButtons()}</p>}
		</div>;
	}

	handleDownloadReplay = (e: MouseEvent) => {
		let room = this.props.room;
		const target = e.currentTarget as HTMLAnchorElement;
		// download replay
		let filename = (room.battle.tier || 'Battle').replace(/[^A-Za-z0-9]/g, '');
		let date = new Date();
		filename += `-${date.getFullYear()}`;
		filename += `-${date.getMonth() >= 9 ? '' : '0'}${date.getMonth() + 1}`;
		filename += `-${date.getDate() >= 10 ? '' : '0'}${date.getDate()}`;
		filename += '-' + toID(room.battle.p1.name);
		filename += '-' + toID(room.battle.p2.name);
		target.href = window.BattleLog.createReplayFileHref(room);
		target.download = filename + '.html';
		e.stopPropagation();
	};

	override render() {
		this.updateLayout();
		const room = this.props.room;
		const id = `room-${room.id}`;
		const hardcoreStyle = room.battle?.hardcoreMode ? <style
			dangerouslySetInnerHTML={{ __html: `#${id} .battle .turn, #${id} .battle-history { display: none !important; }` }}
		></style> : null;
		const { layout, battleHeight, battleWidth, overlayControls } = this.chooseLayout();
		const overlayVersion = overlayControls && !!room.battle && !!room.side && !!room.request && !room.battle.ended;

		// cherish-bshell-render:begin (patch-battle-shell.py)
		if (BattlePanel.cherishShellOn()) {
			// 단일 세로 흐름: 필드 → 조작부 카드. 로그(ChatLog)는 데이터 통로라 늘 마운트하고 CSS 로 숨긴다.
			// 「배틀 기록 보기」 토글은 2026-09-09 제거 — 글 로그는 어디서도 안 보인다 (사용자 결정).
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<div class="cw-bshell">
					<div class="cw-bfield" style={`height:${battleHeight}px;width:${battleWidth}px`}>
						<BattleDiv room={room} />
					</div>
					<div
						class={`cw-bpanel battle-controls${room.width > 660 ? ' wide-controls' : ''}`}
						style={`width:${battleWidth}px`} role="complementary" aria-label="배틀 조작"
					>
						{this.renderCherishShellBar()}
						{this.renderCherishTourCommentary()}
						{this.renderControls(false, false)}
						{this.renderConnectError()}
					</div>
					{/* 2대2 같은 편 팀 채팅 (patch-team-chat-ui.py, 2026-09-22) — 정의는 저쪽, 호출은 블록 주인인 여기가 든다.
					    ⚠️ **조작부 카드의 형제**여야 한다. 안에 넣으면 renderControls 가 매 턴 통째로 다시 그려지며
					    스크롤 위치와 입력 중이던 글자가 날아간다. 여기는 .cw-bshell 의 셋째 자식이라 색인이 고정이다. */}
					{this.renderCherishTeamChat?.(battleWidth)}
					<ChatLog class="battle-log hasuserlist cw-blog" room={room} noSubscription hasPreempt />
				</div>
			</PSPanelWrapper>;
		}
		// cherish-bshell-render:end
		if (layout === 'scrolling') {
			// low-width-low-height layout
			// TODO: nicer phone horizontal layout
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<ChatLog
					class="battle-log hasuserlist" room={room} noSubscription hasPreempt bottom={0}
				>
					{/* cherish-extraction-nochat: 익스트랙션 판은 익명 — 유저 목록·채팅 입력줄을 안 그린다 (patch-extraction-nochat.py) */}
					{room.id.startsWith('battle-gen9cherishextraction-') ? null : <div style="height:18px;position:relative">
						<ChatUserList room={room} top={0} minimized />
					</div>}
					{room.id.startsWith('battle-gen9cherishextraction-') ? null : <ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} left={0} tinyLayout={room.width < 400} />}
					<div style={`height:${battleHeight}px;width:${battleWidth}px;margin: 0 auto;position:relative`}>
						<BattleDiv room={room} />
					</div>
					{overlayVersion && <div class="overlay-controls" style="position:relative;height:0">
						{this.renderControls(true)}
					</div>}
					<div
						class={`battle-controls inline-battle${room.width > 660 ? ' wide-controls' : ''}`}
						role="complementary" aria-label="배틀 조작"
					>
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</ChatLog>
				{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
					<TimerButton room={room} top={7} />}
				<div class="battle-controls-container"></div>
			</PSPanelWrapper>;
		}

		if (layout === 'top-and-bottom') {
			// phone vertical layout
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<div style={`position:relative;height:${battleHeight}px;width:${battleWidth}px;margin:0 auto`}>
					<BattleDiv room={room} />
				</div>
				{overlayVersion && <div
					class="overlay-controls"
					style={`position:absolute;left:0;top:${battleHeight}px;width:100%;height:0`}
				>
					{this.renderControls(true)}
				</div>}
				<ChatLog
					class="battle-log hasuserlist" room={room} top={battleHeight} noSubscription hasPreempt
				>
					<div
						class={`battle-controls${room.width > 660 ? ' wide-controls' : ''}`}
						role="complementary" aria-label="배틀 조작"
					>
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</ChatLog>
				<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} left={0} tinyLayout={room.width < 400} />
				<ChatUserList room={room} top={battleHeight} minimized />
				{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
					<TimerButton room={room} top={battleHeight + 7} />}
				<div class="battle-controls-container"></div>
			</PSPanelWrapper>;
		}

		if (room.width < 500) {
			// oldclient phone layout
			const showingChat = this.mobileChatShown;
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<div class="scrollable-battle-container" style={`width:${battleWidth}px;${showingChat ? 'display:none;' : ''}`}>
					<BattleDiv room={room} />
					{overlayVersion && <div
						class="overlay-controls"
						style={`position:absolute;left:0;top:${battleHeight}px;width:${battleWidth}px;height:0`}
					>
						{this.renderControls(true)}
					</div>}
					<div class="battle-controls-container">
						<div
							class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''}`}
							role="complementary" aria-label="배틀 조작"
							style={`top:${battleHeight + 10}px;width:${battleWidth}px;`}
						>
							{(room.battle && !room.battle.ended && room.request &&
								room.battle.mySide.id === PS.user.userid) && <TimerButton room={room} top={0} />}
							{this.renderControls(false, overlayVersion)}
							{this.renderConnectError()}
						</div>
					</div>
				</div>
				<div style={!showingChat ? 'display:none;' : ''}>
					<ChatLog class="battle-log hasuserlist" room={room} noSubscription hasPreempt />
					<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} tinyLayout />
					<ChatUserList room={room} minimized />
				</div>
				{showingChat ? (
					<button class="battle-chat-toggle button" name="hideChat" onClick={this.showMobileBattle}>
						Battle <i class="fa fa-caret-right" aria-hidden></i>
					</button>
				) : (
					<button class="battle-chat-toggle button" name="showChat" onClick={this.showMobileChat}>
						<i class="fa fa-caret-left" aria-hidden></i> Chat
					</button>
				)}
			</PSPanelWrapper>;
		}

		// regular layout
		return <PSPanelWrapper room={room} focusClick noScroll="hidden">
			{hardcoreStyle}
			<div class="scrollable-battle-container" style={`width:${battleWidth}px`}>
				<BattleDiv room={room} />
				{overlayVersion && <div
					class="overlay-controls"
					style={`position:absolute;left:0;top:${battleHeight}px;width:${battleWidth}px;height:0`}
				>
					{this.renderControls(true)}
				</div>}
				<div class="battle-controls-container">
					<div
						class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''}`}
						role="complementary" aria-label="배틀 조작"
						style={`top:${battleHeight + 10}px;width:${battleWidth}px;`}
					>
						{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
							<TimerButton room={room} top={0} />}
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</div>
			</div>
			<ChatLog
				class="battle-log hasuserlist" room={room} left={battleWidth} noSubscription hasPreempt
			>
				{}
			</ChatLog>
			<ChatTextEntry
				room={room} onMessage={this.send} onKey={this.onKey} left={battleWidth} tinyLayout={room.width < battleWidth + 340}
			/>
			<ChatUserList room={room} left={battleWidth} minimized />
		</PSPanelWrapper>;
	}
}

PS.addRoomType(BattlePanel, BattlesPanel);
